// src/capabilities.ts
function probe(fn) {
  try {
    fn();
    return true;
  } catch {
    return false;
  }
}
var cache = /* @__PURE__ */ new WeakMap();
function makeCapabilities(fields) {
  const record = fields;
  return {
    ...fields,
    has(feature) {
      return record[feature] === true;
    }
  };
}
function computeCapabilities(mod, world) {
  const cached = cache.get(world);
  if (cached) return cached;
  const exp = mod.exports;
  const handle = world.handle;
  const explode = probe(() => exp.b3bridge_explode(handle, 0, -9e3, 0, 0.01, 0, 0));
  const castRay = probe(() => {
    const ptr = mod.malloc(5 * 4);
    try {
      exp.b3bridge_cast_ray_closest(handle, 0, -9e3, 0, 0, 1, 0, ptr);
    } finally {
      mod.free(ptr);
    }
  });
  const awakeBodyCount = probe(() => exp.b3bridge_get_awake_body_count(handle));
  const bodyCount = probe(() => exp.b3bridge_get_body_count(handle));
  const contactEndEvents = typeof exp.b3bridge_drain_contact_end_events === "function";
  const contactHitEvents = typeof exp.b3bridge_drain_contact_hit_events === "function";
  const contactShapeIdentity = typeof exp.b3bridge_get_shape_identity === "function" && typeof exp.b3bridge_drain_contact_begin_events_with_shapes === "function" && typeof exp.b3bridge_drain_contact_end_events_with_shapes === "function" && typeof exp.b3bridge_drain_contact_hit_events_with_shapes === "function";
  const jointMotorTelemetry = typeof exp.b3bridge_get_revolute_motor_torque === "function" && typeof exp.b3bridge_get_spherical_motor_torque === "function";
  const contactLoadTelemetry = typeof exp.b3bridge_get_body_contact_load === "function";
  const convexHull = typeof exp.b3bridge_add_hull_shape === "function";
  const aabbOverlap = typeof exp.b3bridge_overlap_aabb === "function";
  const setGravity = probe(() => {
    const g = exp.b3bridge_setGravity(handle, 0, -9.81, 0);
    void g;
  });
  let setBodyType = false;
  let getLinearVelocity = false;
  let angularVelocity = false;
  let forces = false;
  let forceAtPoint = false;
  let setAwake = false;
  let setBodyTransform = false;
  let bodyQueries = false;
  let shapeMaterial = false;
  let bodyInertia = false;
  let setBodyInertia = false;
  let jointMotors = false;
  let filterJoint = false;
  let probeBody = 0;
  let probeBodyB = 0;
  try {
    probeBody = exp.b3bridge_create_body(handle, 2, 0, -8800, 0, 0, 0, 0, 1, 0, 0, 0, 1);
    probeBodyB = exp.b3bridge_create_body(handle, 2, 0, -8802, 0, 0, 0, 0, 1, 0, 0, 0, 1);
    if (probeBodyB) exp.b3bridge_add_sphere_shape(probeBodyB, 0.1, 1, 0.6, 0, 0);
    if (probeBody) {
      const probeShape = exp.b3bridge_add_sphere_shape(probeBody, 0.1, 1, 0.6, 0, 0);
      setBodyType = probe(() => exp.b3bridge_set_body_type(probeBody, 2));
      getLinearVelocity = probe(() => {
        const ptr = mod.malloc(3 * 4);
        try {
          exp.b3bridge_get_linear_velocity(probeBody, ptr);
        } finally {
          mod.free(ptr);
        }
      });
      angularVelocity = probe(() => {
        exp.b3bridge_setAngularVelocity(probeBody, 0, 0, 0);
        const ptr = mod.malloc(3 * 4);
        try {
          exp.b3bridge_getAngularVelocity(probeBody, ptr);
        } finally {
          mod.free(ptr);
        }
      });
      forces = probe(() => {
        exp.b3bridge_applyForce(probeBody, 0, 0, 0);
        exp.b3bridge_applyTorque(probeBody, 0, 0, 0);
      });
      forceAtPoint = probe(() => exp.b3bridge_applyForceAt(probeBody, 0, 0, 0, 0, -8800, 0));
      setAwake = probe(() => exp.b3bridge_set_awake(probeBody, 0));
      setBodyTransform = probe(
        () => exp.b3bridge_setBodyTransform(probeBody, 0, -8800, 0, 0, 0, 0, 1)
      );
      bodyQueries = probe(() => {
        exp.b3bridge_getBodyType(probeBody);
        exp.b3bridge_isBodyAwake(probeBody);
      });
      shapeMaterial = probe(() => {
        exp.b3bridge_setShapeFriction(probeShape, 0.6);
        exp.b3bridge_setShapeRestitution(probeShape, 0);
      });
      bodyInertia = probe(() => {
        const ptr = mod.malloc(3 * 4);
        try {
          exp.b3bridge_getBodyInertia(probeBody, ptr);
        } finally {
          mod.free(ptr);
        }
      });
      setBodyInertia = probe(() => {
        const ptr = mod.malloc(3 * 4);
        try {
          exp.b3bridge_getBodyInertia(probeBody, ptr);
          const heap = mod.HEAPF32;
          const index = ptr >> 2;
          exp.b3bridge_setBodyInertia(
            probeBody,
            heap[index],
            heap[index + 1],
            heap[index + 2]
          );
        } finally {
          mod.free(ptr);
        }
      });
      if (probeBodyB) {
        jointMotors = probe(() => {
          const revJoint = exp.b3bridge_create_revolute_joint(
            handle,
            probeBody,
            probeBodyB,
            0,
            -8800,
            0,
            0,
            0,
            1,
            0,
            0,
            0,
            1,
            1,
            10
          );
          if (!revJoint) throw new Error("no revolute joint");
          exp.b3bridge_set_revolute_motor(revJoint, 1, 2, 20);
          exp.b3bridge_destroyJoint(revJoint);
          const sphJoint = exp.b3bridge_create_spherical_joint(
            handle,
            probeBody,
            probeBodyB,
            0,
            -8800,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0.7,
            1,
            0,
            1,
            0,
            10
          );
          if (!sphJoint) throw new Error("no spherical joint");
          exp.b3bridge_set_spherical_motor(sphJoint, 1, 0, 2, 0, 20);
          exp.b3bridge_destroyJoint(sphJoint);
        });
        filterJoint = probe(() => {
          const joint = exp.b3bridge_create_filter_joint(handle, probeBody, probeBodyB);
          if (!joint) throw new Error("no filter joint");
          exp.b3bridge_destroyJoint(joint);
        });
      }
    }
  } finally {
    if (probeBody) exp.b3bridge_destroy_body(probeBody);
    if (probeBodyB) exp.b3bridge_destroy_body(probeBodyB);
  }
  const caps = makeCapabilities({
    awakeBodyCount,
    bodyCount,
    setAwake,
    setBodyType,
    getLinearVelocity,
    castRay,
    explode,
    angularVelocity,
    forces,
    setBodyTransform,
    forceAtPoint,
    bodyQueries,
    setGravity,
    shapeMaterial,
    bodyInertia,
    setBodyInertia,
    jointMotors,
    filterJoint,
    jointMotorTelemetry,
    contactEndEvents,
    contactHitEvents,
    contactShapeIdentity,
    contactLoadTelemetry,
    aabbOverlap,
    convexHull
  });
  cache.set(world, caps);
  return caps;
}

// src/types.ts
var DEFAULT_HULL_MAX_VERTICES = 64;
var HULL_MAX_VERTICES_LIMIT = 255;
var BODY_TYPE_TO_INT = {
  static: 0,
  kinematic: 1,
  dynamic: 2
};
var INT_TO_BODY_TYPE = {
  0: "static",
  1: "kinematic",
  2: "dynamic"
};

// src/wasm-loader.ts
var PAGE_SIZE = 65536;
var textDecoder = new TextDecoder();
function isNode() {
  return typeof process !== "undefined" && process.versions != null && process.versions.node != null && typeof globalThis.window === "undefined";
}
function defaultWasmUrl() {
  return new URL("../wasm/box3d.wasm", import.meta.url);
}
function toArrayBuffer(bin) {
  if (bin instanceof Uint8Array) {
    if (bin.buffer instanceof ArrayBuffer && bin.byteOffset === 0 && bin.byteLength === bin.buffer.byteLength) {
      return bin.buffer;
    }
    return bin.slice().buffer;
  }
  return bin;
}
async function resolveWasmBytes(options) {
  if (options.wasmBinary != null) {
    return toArrayBuffer(options.wasmBinary);
  }
  const url = options.wasmUrl != null ? options.wasmUrl : options.locateFile != null ? options.locateFile("box3d.wasm") : defaultWasmUrl();
  if (isNode()) {
    const { readFile } = await import('fs/promises');
    const { fileURLToPath } = await import('url');
    const path = url instanceof URL ? fileURLToPath(url) : url.startsWith("file:") ? fileURLToPath(url) : url;
    const buf = await readFile(path);
    return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
  }
  const response = await fetch(url instanceof URL ? url.href : url);
  if (!response.ok) {
    throw new Error(
      `box3d-web: failed to fetch WASM from ${String(url)} (HTTP ${response.status}). Check the wasmUrl / locateFile override or your bundler's asset handling.`
    );
  }
  return response;
}
async function loadBox3DModule(options = {}) {
  let memory = null;
  const view = () => new DataView(memory.buffer);
  const emscripten_resize_heap = (requestedSize) => {
    if (memory == null) return 0;
    const oldPages = memory.buffer.byteLength / PAGE_SIZE;
    const requestedPages = Math.ceil(requestedSize / PAGE_SIZE);
    if (requestedPages <= oldPages) return 1;
    try {
      memory.grow(requestedPages - oldPages);
      return 1;
    } catch {
      return 0;
    }
  };
  const clock_time_get = (_clockId, _precision, timePtr) => {
    if (memory == null) return 1;
    view().setBigUint64(timePtr, BigInt(Date.now()) * 1000000n, true);
    return 0;
  };
  const fd_write = (_fd, iovs, iovsLen, nwritten) => {
    if (memory == null) return 1;
    const dv = view();
    const heap = new Uint8Array(memory.buffer);
    let written = 0;
    let text = "";
    for (let i = 0; i < iovsLen; ++i) {
      const ptr = dv.getUint32(iovs + i * 8, true);
      const len = dv.getUint32(iovs + i * 8 + 4, true);
      written += len;
      text += textDecoder.decode(heap.subarray(ptr, ptr + len));
    }
    if (text.trim()) console.log(text.trimEnd());
    dv.setUint32(nwritten, written, true);
    return 0;
  };
  const imports = {
    env: {
      emscripten_get_now: () => typeof performance !== "undefined" ? performance.now() : Date.now(),
      emscripten_resize_heap,
      // Supplied defensively: a growth-notify build imports this; if absent it's
      // simply never called. Its presence prevents a LinkError on such builds.
      emscripten_notify_memory_growth: () => {
      }
    },
    wasi_snapshot_preview1: {
      clock_time_get,
      fd_write
    }
  };
  let source;
  try {
    source = await resolveWasmBytes(options);
  } catch (err) {
    throw new Error(
      `box3d-web: could not load the WASM binary. ${err.message}`,
      { cause: err }
    );
  }
  let instance;
  try {
    if (source instanceof Response) {
      const contentType = source.headers.get("content-type");
      if (WebAssembly.instantiateStreaming && contentType === "application/wasm") {
        ({ instance } = await WebAssembly.instantiateStreaming(source, imports));
      } else {
        const bytes = await source.arrayBuffer();
        ({ instance } = await WebAssembly.instantiate(bytes, imports));
      }
    } else {
      ({ instance } = await WebAssembly.instantiate(source, imports));
    }
  } catch (err) {
    throw new Error(
      `box3d-web: WASM instantiation/link failed. This usually means a missing env import or a wrong content-type. Underlying error: ${err.message}`,
      { cause: err }
    );
  }
  const rawExports = instance.exports;
  memory = rawExports.memory;
  if (memory == null || !(memory instanceof WebAssembly.Memory)) {
    throw new Error("box3d-web: WASM module did not export `memory`.");
  }
  if (typeof rawExports.__wasm_call_ctors === "function") {
    rawExports.__wasm_call_ctors();
  } else if (typeof rawExports._initialize === "function") {
    rawExports._initialize();
  } else {
    throw new Error(
      "box3d-web: WASM exports neither `__wasm_call_ctors` nor `_initialize`; cannot run static initializers."
    );
  }
  const mem = memory;
  const module = {
    exports: rawExports,
    memory: mem,
    malloc: (size) => rawExports.malloc(size),
    free: (ptr) => rawExports.free(ptr),
    get HEAP8() {
      return new Int8Array(mem.buffer);
    },
    get HEAPU8() {
      return new Uint8Array(mem.buffer);
    },
    get HEAP32() {
      return new Int32Array(mem.buffer);
    },
    get HEAPU32() {
      return new Uint32Array(mem.buffer);
    },
    get HEAPF32() {
      return new Float32Array(mem.buffer);
    }
  };
  return module;
}

// src/world.ts
function finiteOr(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}
function isVec3Tuple(value) {
  return Array.isArray(value) && value.length === 3 && Number.isFinite(value[0]) && Number.isFinite(value[1]) && Number.isFinite(value[2]);
}
function boolInt(value) {
  return value ? 1 : 0;
}
var Scratch = class {
  constructor(mod) {
    this.mod = mod;
  }
  mod;
  ptr = 0;
  bytes = 0;
  ensure(bytes) {
    if (this.bytes < bytes) {
      if (this.ptr) this.mod.free(this.ptr);
      this.ptr = this.mod.malloc(bytes);
      this.bytes = bytes;
    }
    return this.ptr;
  }
  dispose() {
    if (this.ptr) this.mod.free(this.ptr);
    this.ptr = 0;
    this.bytes = 0;
  }
};
var EVENT_DRAIN_CAPACITY = 64;
function rotateVector(vector, rotation) {
  const [x, y, z] = vector;
  const [qx, qy, qz, qw] = rotation;
  const norm = qx * qx + qy * qy + qz * qz + qw * qw;
  if (!(norm > 1e-12) || !Number.isFinite(norm)) return [x, y, z];
  const s = 2 / norm;
  const xx = qx * qx * s;
  const yy = qy * qy * s;
  const zz = qz * qz * s;
  const xy = qx * qy * s;
  const xz = qx * qz * s;
  const yz = qy * qz * s;
  const wx = qw * qx * s;
  const wy = qw * qy * s;
  const wz = qw * qz * s;
  return [
    (1 - yy - zz) * x + (xy - wz) * y + (xz + wy) * z,
    (xy + wz) * x + (1 - xx - zz) * y + (yz - wx) * z,
    (xz - wy) * x + (yz + wx) * y + (1 - xx - yy) * z
  ];
}
function inverseTransformPoint(point, position, rotation) {
  return rotateVector(
    [point[0] - position[0], point[1] - position[1], point[2] - position[2]],
    [-rotation[0], -rotation[1], -rotation[2], rotation[3]]
  );
}
function transformPoint(local, position, rotation) {
  const rotated = rotateVector(local, rotation);
  return [position[0] + rotated[0], position[1] + rotated[1], position[2] + rotated[2]];
}
var WorldImpl = class {
  handle;
  mod;
  disposed = false;
  idsScratch;
  transformsScratch;
  vectorScratch;
  eventsScratch;
  overlapScratch;
  pointJointMetadata = /* @__PURE__ */ new Map();
  contactShapeIdentity;
  // JS-side accumulate-until-drained queues. Flat for cheap drainInto.
  // On shape-aware builds these queues contain the detailed native tuples. The
  // legacy drains project their old layouts from these same queues, so callers
  // cannot drain one semantic event twice through two APIs.
  // contact begin (legacy 3 / detailed 9): [bodyA, bodyB, ...]
  // contact end   (legacy 2 / detailed 8): [bodyA, bodyB, ...]
  // contact hit   (legacy 9 / detailed 15): [bodyA, bodyB, ...]
  // sensor:        [sensor, other] pairs
  contactQueue = [];
  contactEndQueue = [];
  contactHitQueue = [];
  sensorQueue = [];
  constructor(mod, handle) {
    this.mod = mod;
    this.handle = handle;
    this.idsScratch = new Scratch(mod);
    this.transformsScratch = new Scratch(mod);
    this.vectorScratch = new Scratch(mod);
    this.eventsScratch = new Scratch(mod);
    this.overlapScratch = new Scratch(mod);
    this.contactShapeIdentity = typeof mod.exports.b3bridge_get_shape_identity === "function" && typeof mod.exports.b3bridge_drain_contact_begin_events_with_shapes === "function" && typeof mod.exports.b3bridge_drain_contact_end_events_with_shapes === "function" && typeof mod.exports.b3bridge_drain_contact_hit_events_with_shapes === "function";
  }
  assertLive() {
    if (this.disposed) {
      throw new Error("box3d-web: World used after destroy().");
    }
  }
  capturePointJointMetadata(bodyA, bodyB, worldAnchor) {
    const ids = new Int32Array([bodyA, bodyB]);
    const transforms = new Float32Array(14);
    this.readTransforms(ids, transforms);
    const poseA = [transforms[0], transforms[1], transforms[2]];
    const rotationA = [transforms[3], transforms[4], transforms[5], transforms[6]];
    const poseB = [transforms[7], transforms[8], transforms[9]];
    const rotationB = [transforms[10], transforms[11], transforms[12], transforms[13]];
    return {
      bodyA,
      bodyB,
      localAnchorA: inverseTransformPoint(worldAnchor, poseA, rotationA),
      localAnchorB: inverseTransformPoint(worldAnchor, poseB, rotationB)
    };
  }
  prunePointJointMetadata(body) {
    for (const [joint, metadata] of this.pointJointMetadata) {
      if (metadata.bodyA === body || metadata.bodyB === body) {
        this.pointJointMetadata.delete(joint);
      }
    }
  }
  // --- lifecycle ---
  step(dt, substeps = 4) {
    this.assertLive();
    this.mod.exports.b3bridge_step(this.handle, finiteOr(dt, 1 / 60), substeps | 0);
    this.pullEvents();
  }
  /** Change the world's full gravity vector (x,y,z) at runtime. */
  setGravity(gravity) {
    this.assertLive();
    const [x, y, z] = gravity;
    this.mod.exports.b3bridge_setGravity(this.handle, finiteOr(x, 0), finiteOr(y, -9.81), finiteOr(z, 0));
  }
  /** Read the step's contact/sensor events out of the bridge into the JS queues. */
  pullEvents() {
    if (this.contactShapeIdentity) {
      this.readBridgeEvents(9, "b3bridge_drain_contact_begin_events_with_shapes", (heap, base, count) => {
        for (let i = 0; i < count; i++) {
          const o = base + i * 9;
          for (let j = 0; j < 9; j++) this.contactQueue.push(heap[o + j]);
        }
      });
      this.readBridgeEvents(8, "b3bridge_drain_contact_end_events_with_shapes", (heap, base, count) => {
        for (let i = 0; i < count; i++) {
          const o = base + i * 8;
          for (let j = 0; j < 8; j++) this.contactEndQueue.push(heap[o + j]);
        }
      });
      this.readBridgeEvents(15, "b3bridge_drain_contact_hit_events_with_shapes", (heap, base, count) => {
        for (let i = 0; i < count; i++) {
          const o = base + i * 15;
          for (let j = 0; j < 15; j++) this.contactHitQueue.push(heap[o + j]);
        }
      });
    } else {
      this.readBridgeEvents(3, "b3bridge_drain_contact_begin_events", (heap, base, count) => {
        for (let i = 0; i < count; i++) {
          const o = base + i * 3;
          this.contactQueue.push(heap[o] | 0, heap[o + 1] | 0, heap[o + 2]);
        }
      });
      if (typeof this.mod.exports.b3bridge_drain_contact_end_events === "function") {
        this.readBridgeEvents(2, "b3bridge_drain_contact_end_events", (heap, base, count) => {
          for (let i = 0; i < count; i++) {
            const o = base + i * 2;
            this.contactEndQueue.push(heap[o] | 0, heap[o + 1] | 0);
          }
        });
      }
      if (typeof this.mod.exports.b3bridge_drain_contact_hit_events === "function") {
        this.readBridgeEvents(9, "b3bridge_drain_contact_hit_events", (heap, base, count) => {
          for (let i = 0; i < count; i++) {
            const o = base + i * 9;
            this.contactHitQueue.push(
              heap[o] | 0,
              heap[o + 1] | 0,
              heap[o + 2],
              heap[o + 3],
              heap[o + 4],
              heap[o + 5],
              heap[o + 6],
              heap[o + 7],
              heap[o + 8]
            );
          }
        });
      }
    }
    this.readBridgeEvents(2, "b3bridge_drain_sensor_events", (heap, base, count) => {
      for (let i = 0; i < count; i++) {
        const o = base + i * 2;
        this.sensorQueue.push(heap[o] | 0, heap[o + 1] | 0);
      }
    });
  }
  readBridgeEvents(tupleSize, exportName, consume) {
    const drain = this.mod.exports[exportName];
    if (typeof drain !== "function") return;
    let capacity = EVENT_DRAIN_CAPACITY;
    for (; ; ) {
      const ptr = this.eventsScratch.ensure(capacity * tupleSize * 4);
      const total = drain(this.handle, ptr, capacity);
      if (total > capacity) {
        capacity = total;
        continue;
      }
      if (total > 0) {
        consume(this.mod.HEAPF32, ptr >> 2, total);
      }
      break;
    }
  }
  createBody(options = {}) {
    this.assertLive();
    const {
      type = "dynamic",
      position,
      rotation,
      ccd = false,
      linearDamping = 0,
      angularDamping = 0,
      gravityScale = 1
    } = options;
    const [x, y, z] = position ?? [0, 0, 0];
    const [qx, qy, qz, qw] = rotation ?? [0, 0, 0, 1];
    const typeInt = BODY_TYPE_TO_INT[type] ?? BODY_TYPE_TO_INT.dynamic;
    return this.mod.exports.b3bridge_create_body(
      this.handle,
      typeInt,
      finiteOr(x, 0),
      finiteOr(y, 0),
      finiteOr(z, 0),
      finiteOr(qx, 0),
      finiteOr(qy, 0),
      finiteOr(qz, 0),
      finiteOr(qw, 1),
      boolInt(ccd),
      Math.max(0, finiteOr(linearDamping, 0)),
      Math.max(0, finiteOr(angularDamping, 0)),
      finiteOr(gravityScale, 1)
    );
  }
  destroyBody(body) {
    this.assertLive();
    this.mod.exports.b3bridge_destroy_body(body);
    this.prunePointJointMetadata(body);
  }
  setBodyType(body, type) {
    this.assertLive();
    this.mod.exports.b3bridge_set_body_type(body, BODY_TYPE_TO_INT[type] ?? BODY_TYPE_TO_INT.dynamic);
  }
  /** Returns the body's current type, or `null` for an invalid handle or an
   *  older WASM build that predates this export (bridge round 2). */
  getBodyType(body) {
    this.assertLive();
    const raw = this.mod.exports.b3bridge_getBodyType(body);
    if (raw < 0) return null;
    return INT_TO_BODY_TYPE[raw] ?? null;
  }
  /** Returns whether the body is currently awake (bridge round 2). */
  isBodyAwake(body) {
    this.assertLive();
    return this.mod.exports.b3bridge_isBodyAwake(body) !== 0;
  }
  setBodyTransform(body, position, rotation) {
    this.assertLive();
    const [x, y, z] = position;
    const [qx, qy, qz, qw] = rotation;
    this.mod.exports.b3bridge_setBodyTransform(
      body,
      finiteOr(x, 0),
      finiteOr(y, 0),
      finiteOr(z, 0),
      finiteOr(qx, 0),
      finiteOr(qy, 0),
      finiteOr(qz, 0),
      finiteOr(qw, 1)
    );
  }
  // --- shapes ---
  addBox(body, half, material = {}) {
    this.assertLive();
    const { density = 1, friction = 0.6, restitution = 0, rollingResistance = 0 } = material;
    const [hx, hy, hz] = half;
    return this.mod.exports.b3bridge_add_box_shape(
      body,
      finiteOr(hx, 0.5),
      finiteOr(hy, 0.5),
      finiteOr(hz, 0.5),
      finiteOr(density, 1),
      finiteOr(friction, 0.6),
      finiteOr(restitution, 0),
      Math.max(0, finiteOr(rollingResistance, 0))
    );
  }
  addSphere(body, radius, material = {}) {
    this.assertLive();
    const { density = 1, friction = 0.6, restitution = 0, rollingResistance = 0 } = material;
    return this.mod.exports.b3bridge_add_sphere_shape(
      body,
      finiteOr(radius, 0.5),
      finiteOr(density, 1),
      finiteOr(friction, 0.6),
      finiteOr(restitution, 0),
      Math.max(0, finiteOr(rollingResistance, 0))
    );
  }
  addCapsule(body, radius, halfHeight, material = {}) {
    this.assertLive();
    const { density = 1, friction = 0.6, restitution = 0, rollingResistance = 0 } = material;
    return this.mod.exports.b3bridge_add_capsule_shape(
      body,
      finiteOr(radius, 0.2),
      finiteOr(halfHeight, 0.5),
      finiteOr(density, 1),
      finiteOr(friction, 0.6),
      finiteOr(restitution, 0),
      Math.max(0, finiteOr(rollingResistance, 0))
    );
  }
  addSensorBox(body, half) {
    this.assertLive();
    const [hx, hy, hz] = half;
    return this.mod.exports.b3bridge_add_sensor_box_shape(
      body,
      finiteOr(hx, 0.5),
      finiteOr(hy, 0.5),
      finiteOr(hz, 0.5)
    );
  }
  /**
   * Attach a **convex** hull collider built from body-local points.
   *
   * Computes one convex hull — concavity is not preserved. For concave meshes,
   * pre-decompose into multiple convex hulls (CoACD recommended) and call this
   * once per piece. Default `maxVertices` is 64 (good for demolition fragments);
   * Box3D hard-caps at 255.
   *
   * Accepts either `readonly Vec3[]` or a packed `Float32Array` of XYZ triples.
   * Throws on invalid input or native hull-build failure (never returns a fake
   * zero handle as success).
   */
  addHull(body, points, options = {}) {
    this.assertLive();
    const {
      density = 1,
      friction = 0.6,
      restitution = 0,
      rollingResistance = 0,
      maxVertices = DEFAULT_HULL_MAX_VERTICES
    } = options;
    if (!Number.isInteger(maxVertices) || maxVertices < 4 || maxVertices > HULL_MAX_VERTICES_LIMIT) {
      throw new RangeError(
        `box3d-web: addHull maxVertices must be an integer in [4, ${HULL_MAX_VERTICES_LIMIT}] (got ${String(maxVertices)}).`
      );
    }
    let pointCount;
    let writeToHeap;
    if (points instanceof Float32Array) {
      if (points.length < 12 || points.length % 3 !== 0) {
        throw new RangeError(
          `box3d-web: addHull Float32Array must hold \u2265 4 XYZ triples (length multiple of 3, \u2265 12; got length ${points.length}).`
        );
      }
      pointCount = points.length / 3;
      for (let i = 0; i < points.length; i++) {
        if (!Number.isFinite(points[i])) {
          throw new RangeError(
            `box3d-web: addHull points must be finite (non-finite at float index ${i}).`
          );
        }
      }
      writeToHeap = (heap, baseF32) => {
        heap.set(points, baseF32);
      };
    } else if (Array.isArray(points)) {
      if (points.length < 4) {
        throw new RangeError(
          `box3d-web: addHull requires at least 4 points (got ${points.length}).`
        );
      }
      pointCount = points.length;
      for (let i = 0; i < pointCount; i++) {
        const p = points[i];
        if (!isVec3Tuple(p)) {
          throw new TypeError(
            `box3d-web: addHull points[${i}] must be a finite Vec3 [x, y, z].`
          );
        }
      }
      writeToHeap = (heap, baseF32) => {
        for (let i = 0; i < pointCount; i++) {
          const p = points[i];
          const o = baseF32 + i * 3;
          heap[o] = p[0];
          heap[o + 1] = p[1];
          heap[o + 2] = p[2];
        }
      };
    } else {
      throw new TypeError(
        "box3d-web: addHull points must be readonly Vec3[] or a packed Float32Array."
      );
    }
    const addHullShape = this.mod.exports.b3bridge_add_hull_shape;
    if (typeof addHullShape !== "function") {
      throw new Error(
        "box3d-web: addHull requires Capabilities.convexHull (WASM export b3bridge_add_hull_shape missing \u2014 rebuild native)."
      );
    }
    const bytes = pointCount * 3 * 4;
    const ptr = this.mod.malloc(bytes);
    if (!ptr) {
      throw new Error("box3d-web: addHull failed to allocate WASM point buffer.");
    }
    try {
      writeToHeap(this.mod.HEAPF32, ptr >> 2);
      const shape = addHullShape(
        body,
        ptr,
        pointCount,
        maxVertices,
        finiteOr(density, 1),
        finiteOr(friction, 0.6),
        finiteOr(restitution, 0),
        Math.max(0, finiteOr(rollingResistance, 0))
      );
      if (shape === 0) {
        throw new Error(
          "box3d-web: addHull native hull build failed (invalid body, degenerate/coplanar point cloud, or shape pool full)."
        );
      }
      return shape;
    } finally {
      this.mod.free(ptr);
    }
  }
  /** Update a shape's friction after creation (bridge round 2). */
  setShapeFriction(shape, friction) {
    this.assertLive();
    this.mod.exports.b3bridge_setShapeFriction(shape, finiteOr(friction, 0.6));
  }
  /** Update a shape's restitution after creation (bridge round 2). */
  setShapeRestitution(shape, restitution) {
    this.assertLive();
    this.mod.exports.b3bridge_setShapeRestitution(shape, finiteOr(restitution, 0));
  }
  // --- velocities / forces / impulses / kinematics ---
  setLinearVelocity(body, v) {
    this.assertLive();
    this.mod.exports.b3bridge_set_linear_velocity(
      body,
      finiteOr(v[0], 0),
      finiteOr(v[1], 0),
      finiteOr(v[2], 0)
    );
  }
  getLinearVelocity(body, out) {
    this.assertLive();
    return this.readVec3(this.mod.exports.b3bridge_get_linear_velocity, body, out);
  }
  setAngularVelocity(body, w) {
    this.assertLive();
    this.mod.exports.b3bridge_setAngularVelocity(
      body,
      finiteOr(w[0], 0),
      finiteOr(w[1], 0),
      finiteOr(w[2], 0)
    );
  }
  getAngularVelocity(body, out) {
    this.assertLive();
    return this.readVec3(this.mod.exports.b3bridge_getAngularVelocity, body, out);
  }
  setLinearDamping(body, damping) {
    this.assertLive();
    this.mod.exports.b3bridge_setLinearDamping(body, Math.max(0, finiteOr(damping, 0)));
  }
  getLinearDamping(body) {
    this.assertLive();
    return this.mod.exports.b3bridge_getLinearDamping(body);
  }
  setAngularDamping(body, damping) {
    this.assertLive();
    this.mod.exports.b3bridge_setAngularDamping(body, Math.max(0, finiteOr(damping, 0)));
  }
  getAngularDamping(body) {
    this.assertLive();
    return this.mod.exports.b3bridge_getAngularDamping(body);
  }
  setGravityScale(body, scale) {
    this.assertLive();
    this.mod.exports.b3bridge_setGravityScale(body, finiteOr(scale, 1));
  }
  getGravityScale(body) {
    this.assertLive();
    return this.mod.exports.b3bridge_getGravityScale(body);
  }
  getBodyMass(body) {
    this.assertLive();
    return this.mod.exports.b3bridge_getBodyMass(body);
  }
  getBodyInertia(body, out) {
    this.assertLive();
    return this.readVec3(this.mod.exports.b3bridge_getBodyInertia, body, out);
  }
  setBodyInertia(body, diagonal) {
    this.assertLive();
    const [ixx, iyy, izz] = diagonal;
    if (![ixx, iyy, izz].every((value) => Number.isFinite(value) && value > 0)) {
      throw new RangeError(
        "box3d-web: body inertia diagonal values must be finite and greater than zero."
      );
    }
    this.mod.exports.b3bridge_setBodyInertia(body, ixx, iyy, izz);
  }
  readVec3(fn, handle, out) {
    const ptr = this.vectorScratch.ensure(3 * 4);
    fn(handle, ptr);
    const heap = this.mod.HEAPF32;
    const i = ptr >> 2;
    const x = heap[i];
    const y = heap[i + 1];
    const z = heap[i + 2];
    if (out instanceof Float32Array) {
      if (out.length < 3) {
        throw new RangeError("box3d-web: Vec3 Float32Array out must have length \u2265 3.");
      }
      out[0] = x;
      out[1] = y;
      out[2] = z;
      return out;
    }
    if (out) {
      out.x = x;
      out.y = y;
      out.z = z;
      return out;
    }
    return { x, y, z };
  }
  applyImpulse(body, impulse, at) {
    this.assertLive();
    const [ix, iy, iz] = impulse;
    const [px, py, pz] = at ?? [0, 0, 0];
    this.mod.exports.b3bridge_apply_impulse(
      body,
      finiteOr(ix, 0),
      finiteOr(iy, 0),
      finiteOr(iz, 0),
      finiteOr(px, 0),
      finiteOr(py, 0),
      finiteOr(pz, 0)
    );
  }
  applyImpulseToCenter(body, impulse) {
    this.assertLive();
    this.mod.exports.b3bridge_applyImpulseToCenter(
      body,
      finiteOr(impulse[0], 0),
      finiteOr(impulse[1], 0),
      finiteOr(impulse[2], 0)
    );
  }
  applyForce(body, force, at) {
    this.assertLive();
    if (at && typeof this.mod.exports.b3bridge_applyForceAt === "function") {
      const [px, py, pz] = at;
      this.mod.exports.b3bridge_applyForceAt(
        body,
        finiteOr(force[0], 0),
        finiteOr(force[1], 0),
        finiteOr(force[2], 0),
        finiteOr(px, 0),
        finiteOr(py, 0),
        finiteOr(pz, 0)
      );
      return;
    }
    this.mod.exports.b3bridge_applyForce(
      body,
      finiteOr(force[0], 0),
      finiteOr(force[1], 0),
      finiteOr(force[2], 0)
    );
  }
  applyTorque(body, torque) {
    this.assertLive();
    this.mod.exports.b3bridge_applyTorque(
      body,
      finiteOr(torque[0], 0),
      finiteOr(torque[1], 0),
      finiteOr(torque[2], 0)
    );
  }
  setKinematicTarget(body, position, rotation, dt) {
    this.assertLive();
    const [x, y, z] = position;
    const [qx, qy, qz, qw] = rotation;
    this.mod.exports.b3bridge_set_kinematic_target(
      body,
      finiteOr(x, 0),
      finiteOr(y, 0),
      finiteOr(z, 0),
      finiteOr(qx, 0),
      finiteOr(qy, 0),
      finiteOr(qz, 0),
      finiteOr(qw, 1),
      finiteOr(dt, 1 / 60)
    );
  }
  // --- joints ---
  createSphericalJoint(a, b, options = {}) {
    this.assertLive();
    const [ax, ay, az] = options.anchor ?? [0, 0, 0];
    const hasCone = options.coneLimit != null && Number.isFinite(options.coneLimit);
    const hasTwist = options.twistLimit != null && Number.isFinite(options.twistLimit[0]) && Number.isFinite(options.twistLimit[1]);
    const springHertz = options.spring ? finiteOr(options.spring.hertz, 0) : 0;
    const springDamping = options.spring ? finiteOr(options.spring.dampingRatio ?? 0.7, 0.7) : 0.7;
    const motor = options.motor;
    const [mvx, mvy, mvz] = motor?.velocity ?? [0, 0, 0];
    const joint = this.mod.exports.b3bridge_create_spherical_joint(
      this.handle,
      a,
      b,
      finiteOr(ax, 0),
      finiteOr(ay, 0),
      finiteOr(az, 0),
      boolInt(hasCone),
      hasCone ? options.coneLimit : 0,
      boolInt(hasTwist),
      hasTwist ? options.twistLimit[0] : 0,
      hasTwist ? options.twistLimit[1] : 0,
      springHertz,
      springDamping,
      boolInt(motor != null),
      finiteOr(mvx, 0),
      finiteOr(mvy, 0),
      finiteOr(mvz, 0),
      motor ? Math.max(0, finiteOr(motor.maxTorque, 0)) : 0
    );
    if (joint > 0) {
      this.pointJointMetadata.set(
        joint,
        this.capturePointJointMetadata(a, b, [finiteOr(ax, 0), finiteOr(ay, 0), finiteOr(az, 0)])
      );
    }
    return joint;
  }
  createRevoluteJoint(a, b, options = {}) {
    this.assertLive();
    const [ax, ay, az] = options.anchor ?? [0, 0, 0];
    const [hx, hy, hz] = options.axis ?? [0, 0, 1];
    const hasLimit = options.limit != null && Number.isFinite(options.limit[0]) && Number.isFinite(options.limit[1]);
    const motor = options.motor;
    const joint = this.mod.exports.b3bridge_create_revolute_joint(
      this.handle,
      a,
      b,
      finiteOr(ax, 0),
      finiteOr(ay, 0),
      finiteOr(az, 0),
      finiteOr(hx, 0),
      finiteOr(hy, 0),
      finiteOr(hz, 1),
      boolInt(hasLimit),
      hasLimit ? options.limit[0] : 0,
      hasLimit ? options.limit[1] : 0,
      boolInt(motor != null),
      motor ? finiteOr(motor.speed, 0) : 0,
      motor ? Math.max(0, finiteOr(motor.maxTorque, 0)) : 0
    );
    if (joint > 0) {
      this.pointJointMetadata.set(
        joint,
        this.capturePointJointMetadata(a, b, [finiteOr(ax, 0), finiteOr(ay, 0), finiteOr(az, 0)])
      );
    }
    return joint;
  }
  createDistanceJoint(a, b, options = {}) {
    this.assertLive();
    const [aax, aay, aaz] = options.anchorA ?? [0, 0, 0];
    const [abx, aby, abz] = options.anchorB ?? [0, 0, 0];
    const enableSpring = options.spring != null;
    const hertz = options.spring ? finiteOr(options.spring.hertz, 8) : 8;
    const damping = options.spring ? finiteOr(options.spring.dampingRatio ?? 0.7, 0.7) : 0.7;
    return this.mod.exports.b3bridge_create_distance_joint_ex(
      this.handle,
      a,
      b,
      finiteOr(aax, 0),
      finiteOr(aay, 0),
      finiteOr(aaz, 0),
      finiteOr(abx, 0),
      finiteOr(aby, 0),
      finiteOr(abz, 0),
      finiteOr(options.length ?? 1, 1),
      finiteOr(options.minLength ?? options.length ?? 0.1, 0.1),
      finiteOr(options.maxLength ?? options.length ?? 1, 1),
      boolInt(enableSpring),
      hertz,
      damping,
      boolInt(options.enableLimit ?? false)
    );
  }
  destroyJoint(joint) {
    this.assertLive();
    this.mod.exports.b3bridge_destroyJoint(joint);
    this.pointJointMetadata.delete(joint);
  }
  /**
   * Measure the world-space distance between the two anchors of a spherical or
   * revolute joint. Distance, filter, unknown, and destroyed joints are rejected
   * because they do not represent a point coincidence constraint.
   */
  getJointAnchorSeparation(joint) {
    this.assertLive();
    const metadata = this.pointJointMetadata.get(joint);
    if (!metadata) {
      throw new RangeError(
        "box3d-web: getJointAnchorSeparation requires a live spherical/revolute joint."
      );
    }
    const ids = new Int32Array([metadata.bodyA, metadata.bodyB]);
    const transforms = new Float32Array(14);
    this.readTransforms(ids, transforms);
    if (!Number.isFinite(transforms[0]) || !Number.isFinite(transforms[1]) || !Number.isFinite(transforms[2]) || !Number.isFinite(transforms[7]) || !Number.isFinite(transforms[8]) || !Number.isFinite(transforms[9])) {
      throw new RangeError("box3d-web: getJointAnchorSeparation encountered a destroyed body.");
    }
    const anchorA = transformPoint(
      metadata.localAnchorA,
      [transforms[0], transforms[1], transforms[2]],
      [transforms[3], transforms[4], transforms[5], transforms[6]]
    );
    const anchorB = transformPoint(
      metadata.localAnchorB,
      [transforms[7], transforms[8], transforms[9]],
      [transforms[10], transforms[11], transforms[12], transforms[13]]
    );
    return Math.hypot(
      anchorA[0] - anchorB[0],
      anchorA[1] - anchorB[1],
      anchorA[2] - anchorB[2]
    );
  }
  /**
   * A joint with no constraint that only disables collision between `a` and
   * `b` (v0.5 — see `Capabilities.filterJoint`). Destroy via `destroyJoint`
   * like any other joint to restore collision between the pair.
   */
  createFilterJoint(a, b) {
    this.assertLive();
    return this.mod.exports.b3bridge_create_filter_joint(this.handle, a, b);
  }
  /**
   * Enable/disable and retune a revolute joint's solver-integrated motor after
   * creation (v0.5 — see `Capabilities.jointMotors`). Pass `null` to disable.
   * Unlike an externally-applied torque impulse, the solver enforces
   * `maxTorque` every substep while driving toward `speed`.
   */
  setRevoluteMotor(joint, opts) {
    this.assertLive();
    if (opts == null) {
      this.mod.exports.b3bridge_set_revolute_motor(joint, 0, 0, 0);
      return;
    }
    this.mod.exports.b3bridge_set_revolute_motor(
      joint,
      1,
      finiteOr(opts.speed, 0),
      Math.max(0, finiteOr(opts.maxTorque, 0))
    );
  }
  /**
   * Enable/disable and retune a spherical joint's solver-integrated motor
   * after creation (v0.5 — see `Capabilities.jointMotors`). Pass `null` to
   * disable. Unlike an externally-applied torque impulse, the solver enforces
   * `maxTorque` every substep while driving toward `velocity`.
   */
  setSphericalMotor(joint, opts) {
    this.assertLive();
    if (opts == null) {
      this.mod.exports.b3bridge_set_spherical_motor(joint, 0, 0, 0, 0, 0);
      return;
    }
    const [vx, vy, vz] = opts.velocity;
    this.mod.exports.b3bridge_set_spherical_motor(
      joint,
      1,
      finiteOr(vx, 0),
      finiteOr(vy, 0),
      finiteOr(vz, 0),
      Math.max(0, finiteOr(opts.maxTorque, 0))
    );
  }
  getBodyContactLoad(body, out) {
    this.assertLive();
    const fn = this.mod.exports.b3bridge_get_body_contact_load;
    if (typeof fn !== "function") {
      throw new Error(
        "box3d-web: getBodyContactLoad requires Capabilities.contactLoadTelemetry (WASM export missing)."
      );
    }
    const ptr = this.vectorScratch.ensure(2 * 4);
    fn(body, ptr);
    const heap = this.mod.HEAPF32;
    const i = ptr >> 2;
    const pointCount = heap[i] | 0;
    const totalNormalImpulse = heap[i + 1];
    if (out) {
      out.pointCount = pointCount;
      out.totalNormalImpulse = totalNormalImpulse;
      return out;
    }
    return { pointCount, totalNormalImpulse };
  }
  /**
   * Current revolute motor torque in Newton-meters after the most recent step
   * (`Capabilities.jointMotorTelemetry`). Returns `0` for invalid handles and
   * when the loaded WASM lacks the export.
   */
  getRevoluteMotorTorque(joint) {
    this.assertLive();
    const fn = this.mod.exports.b3bridge_get_revolute_motor_torque;
    if (typeof fn !== "function") return 0;
    return fn(joint);
  }
  getSphericalMotorTorque(joint, out) {
    this.assertLive();
    const fn = this.mod.exports.b3bridge_get_spherical_motor_torque;
    if (typeof fn !== "function") {
      if (out instanceof Float32Array) {
        if (out.length < 3) {
          throw new RangeError("box3d-web: Vec3 Float32Array out must have length \u2265 3.");
        }
        out[0] = 0;
        out[1] = 0;
        out[2] = 0;
        return out;
      }
      if (out) {
        out.x = 0;
        out.y = 0;
        out.z = 0;
        return out;
      }
      return { x: 0, y: 0, z: 0 };
    }
    return this.readVec3(fn, joint, out);
  }
  // --- queries ---
  castRayClosest(origin, dir) {
    this.assertLive();
    const ptr = this.vectorScratch.ensure(5 * 4);
    this.mod.exports.b3bridge_cast_ray_closest(
      this.handle,
      finiteOr(origin[0], 0),
      finiteOr(origin[1], 0),
      finiteOr(origin[2], 0),
      finiteOr(dir[0], 0),
      finiteOr(dir[1], 0),
      finiteOr(dir[2], 0),
      ptr
    );
    const heap = this.mod.HEAPF32;
    const i = ptr >> 2;
    if (heap[i] <= 0.5) return null;
    return {
      body: heap[i + 1] | 0,
      point: { x: heap[i + 2], y: heap[i + 3], z: heap[i + 4] }
    };
  }
  assertAABBOverlapAvailable() {
    const fn = this.mod.exports.b3bridge_overlap_aabb;
    if (typeof fn !== "function") {
      throw new Error(
        "box3d-web: overlapAABB requires Capabilities.aabbOverlap; rebuild the native WASM bridge."
      );
    }
    return fn;
  }
  readAABBBounds(lowerBound, upperBound) {
    const [lowerX, lowerY, lowerZ] = lowerBound;
    const [upperX, upperY, upperZ] = upperBound;
    if (![lowerX, lowerY, lowerZ, upperX, upperY, upperZ].every(Number.isFinite) || lowerX > upperX || lowerY > upperY || lowerZ > upperZ) {
      throw new RangeError("box3d-web: overlapAABB bounds must be finite and lowerBound <= upperBound.");
    }
    return [lowerX, lowerY, lowerZ, upperX, upperY, upperZ];
  }
  /** Find shapes whose broad-phase bounds potentially overlap a world-space
   * AABB. Result order is unspecified; compounds report their outer shape. */
  overlapAABB(lowerBound, upperBound) {
    this.assertLive();
    const fn = this.assertAABBOverlapAvailable();
    const [lowerX, lowerY, lowerZ, upperX, upperY, upperZ] = this.readAABBBounds(lowerBound, upperBound);
    let capacity = EVENT_DRAIN_CAPACITY;
    for (; ; ) {
      const ptr = this.overlapScratch.ensure(capacity * 4 * 4);
      const total = fn(
        this.handle,
        lowerX,
        lowerY,
        lowerZ,
        upperX,
        upperY,
        upperZ,
        ptr,
        capacity
      );
      if (total > capacity) {
        capacity = total;
        continue;
      }
      const heap = this.mod.HEAPF32;
      const base = ptr >> 2;
      const out = [];
      for (let i = 0; i < total; i++) {
        const offset = base + i * 4;
        out.push({
          body: heap[offset] | 0,
          shape: {
            index1: heap[offset + 1],
            world0: heap[offset + 2],
            generation: heap[offset + 3]
          }
        });
      }
      return out;
    }
  }
  /** Writes [bodyHandle, shape.index1, shape.world0, shape.generation] into
   * `out`. Returns the total match count, which may exceed its capacity. */
  overlapAABBInto(lowerBound, upperBound, out) {
    this.assertLive();
    if (!(out instanceof Float32Array)) {
      throw new TypeError("box3d-web: overlapAABBInto `out` must be a Float32Array.");
    }
    const fn = this.assertAABBOverlapAvailable();
    const [lowerX, lowerY, lowerZ, upperX, upperY, upperZ] = this.readAABBBounds(lowerBound, upperBound);
    const capacity = Math.floor(out.length / 4);
    const heap = this.mod.HEAPF32;
    const writesDirect = out.buffer === heap.buffer;
    const ptr = writesDirect ? out.byteOffset : this.overlapScratch.ensure(capacity * 4 * 4);
    const total = fn(
      this.handle,
      lowerX,
      lowerY,
      lowerZ,
      upperX,
      upperY,
      upperZ,
      ptr,
      capacity
    );
    if (!writesDirect && capacity > 0) {
      const currentHeap = this.mod.HEAPF32;
      const writtenFloatCount = Math.min(total, capacity) * 4;
      out.set(currentHeap.subarray(ptr >> 2, (ptr >> 2) + writtenFloatCount));
    }
    return total;
  }
  // --- events ---
  assertShapeIdentityAvailable() {
    if (!this.contactShapeIdentity) {
      throw new Error(
        "box3d-web: shape-aware contact events require Capabilities.contactShapeIdentity; rebuild the native WASM bridge."
      );
    }
  }
  shapeIdentityFromQueue(q, offset) {
    return {
      index1: q[offset],
      world0: q[offset + 1],
      generation: q[offset + 2]
    };
  }
  getShapeIdentity(shape) {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const ptr = this.vectorScratch.ensure(3 * 4);
    this.mod.exports.b3bridge_get_shape_identity(shape, ptr);
    const heap = this.mod.HEAPF32;
    const i = ptr >> 2;
    if (heap[i] === 0 && heap[i + 1] === 0 && heap[i + 2] === 0) return null;
    return {
      index1: heap[i],
      world0: heap[i + 1],
      generation: heap[i + 2]
    };
  }
  drainContactBeginEvents() {
    this.assertLive();
    const q = this.contactQueue;
    const out = [];
    const stride = this.contactShapeIdentity ? 9 : 3;
    for (let i = 0; i < q.length; i += stride) {
      out.push({
        bodyA: q[i],
        bodyB: q[i + 1],
        approachSpeed: q[i + (this.contactShapeIdentity ? 8 : 2)]
      });
    }
    this.contactQueue = [];
    return out;
  }
  drainContactBeginEventsWithShapes() {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactQueue;
    const out = [];
    for (let i = 0; i < q.length; i += 9) {
      out.push({
        bodyA: q[i],
        bodyB: q[i + 1],
        shapeA: this.shapeIdentityFromQueue(q, i + 2),
        shapeB: this.shapeIdentityFromQueue(q, i + 5),
        approachSpeed: q[i + 8]
      });
    }
    this.contactQueue = [];
    return out;
  }
  drainContactEndEvents() {
    this.assertLive();
    const q = this.contactEndQueue;
    const out = [];
    const stride = this.contactShapeIdentity ? 8 : 2;
    for (let i = 0; i < q.length; i += stride) {
      out.push({ bodyA: q[i], bodyB: q[i + 1] });
    }
    this.contactEndQueue = [];
    return out;
  }
  drainContactEndEventsWithShapes() {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactEndQueue;
    const out = [];
    for (let i = 0; i < q.length; i += 8) {
      out.push({
        bodyA: q[i],
        bodyB: q[i + 1],
        shapeA: this.shapeIdentityFromQueue(q, i + 2),
        shapeB: this.shapeIdentityFromQueue(q, i + 5)
      });
    }
    this.contactEndQueue = [];
    return out;
  }
  drainContactHitEvents() {
    this.assertLive();
    const q = this.contactHitQueue;
    const out = [];
    const stride = this.contactShapeIdentity ? 15 : 9;
    const pointOffset = this.contactShapeIdentity ? 8 : 2;
    for (let i = 0; i < q.length; i += stride) {
      out.push({
        bodyA: q[i],
        bodyB: q[i + 1],
        point: { x: q[i + pointOffset], y: q[i + pointOffset + 1], z: q[i + pointOffset + 2] },
        normal: { x: q[i + pointOffset + 3], y: q[i + pointOffset + 4], z: q[i + pointOffset + 5] },
        approachSpeed: q[i + pointOffset + 6]
      });
    }
    this.contactHitQueue = [];
    return out;
  }
  drainContactHitEventsWithShapes() {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactHitQueue;
    const out = [];
    for (let i = 0; i < q.length; i += 15) {
      out.push({
        bodyA: q[i],
        bodyB: q[i + 1],
        shapeA: this.shapeIdentityFromQueue(q, i + 2),
        shapeB: this.shapeIdentityFromQueue(q, i + 5),
        point: { x: q[i + 8], y: q[i + 9], z: q[i + 10] },
        normal: { x: q[i + 11], y: q[i + 12], z: q[i + 13] },
        approachSpeed: q[i + 14]
      });
    }
    this.contactHitQueue = [];
    return out;
  }
  drainSensorEvents() {
    this.assertLive();
    const q = this.sensorQueue;
    const out = [];
    for (let i = 0; i < q.length; i += 2) {
      out.push({ sensor: q[i], other: q[i + 1] });
    }
    this.sensorQueue = [];
    return out;
  }
  /** Writes [bodyA, bodyB, approachSpeed] tuples into `out`. Returns total
   *  accumulated count (may exceed out's tuple capacity — re-call with a bigger
   *  buffer). Drains the shared queue. */
  drainContactBeginEventsInto(out) {
    this.assertLive();
    const q = this.contactQueue;
    const sourceStride = this.contactShapeIdentity ? 9 : 3;
    const total = q.length / sourceStride;
    const cap = Math.floor(out.length / 3);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * sourceStride;
      out[i * 3] = q[src];
      out[i * 3 + 1] = q[src + 1];
      out[i * 3 + 2] = q[src + (this.contactShapeIdentity ? 8 : 2)];
    }
    this.contactQueue = [];
    return total;
  }
  /** Writes detailed begin tuples:
   *  [bodyA, bodyB, a.index1, a.world0, a.generation, b.index1, b.world0,
   *   b.generation, approachSpeed]. Returns total accumulated event count and
   *  drains the shared begin queue. */
  drainContactBeginEventsWithShapesInto(out) {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactQueue;
    const total = q.length / 9;
    const cap = Math.floor(out.length / 9);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * 9;
      for (let j = 0; j < 9; j++) out[src + j] = q[src + j];
    }
    this.contactQueue = [];
    return total;
  }
  /** Writes [bodyA, bodyB] tuples into `out`. Returns total accumulated count
   *  (may exceed out's tuple capacity). Drains the shared end queue. */
  drainContactEndEventsInto(out) {
    this.assertLive();
    const q = this.contactEndQueue;
    const sourceStride = this.contactShapeIdentity ? 8 : 2;
    const total = q.length / sourceStride;
    const cap = Math.floor(out.length / 2);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * sourceStride;
      out[i * 2] = q[src];
      out[i * 2 + 1] = q[src + 1];
    }
    this.contactEndQueue = [];
    return total;
  }
  /** Writes detailed end tuples:
   *  [bodyA, bodyB, a.index1, a.world0, a.generation, b.index1, b.world0,
   *   b.generation]. Returns total accumulated event count and drains the
   *  shared end queue. */
  drainContactEndEventsWithShapesInto(out) {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactEndQueue;
    const total = q.length / 8;
    const cap = Math.floor(out.length / 8);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * 8;
      for (let j = 0; j < 8; j++) out[src + j] = q[src + j];
    }
    this.contactEndQueue = [];
    return total;
  }
  /** Writes
   *  [bodyA, bodyB, pointX, pointY, pointZ, normalX, normalY, normalZ, approachSpeed]
   *  tuples into `out`. Returns total accumulated count (may exceed out's tuple
   *  capacity). Drains the shared hit queue. */
  drainContactHitEventsInto(out) {
    this.assertLive();
    const q = this.contactHitQueue;
    const sourceStride = this.contactShapeIdentity ? 15 : 9;
    const total = q.length / sourceStride;
    const cap = Math.floor(out.length / 9);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * sourceStride;
      const dst = i * 9;
      out[dst] = q[src];
      out[dst + 1] = q[src + 1];
      const sourceOffset = this.contactShapeIdentity ? 8 : 2;
      for (let j = 0; j < 7; j++) out[dst + 2 + j] = q[src + sourceOffset + j];
    }
    this.contactHitQueue = [];
    return total;
  }
  /** Writes detailed hit tuples:
   *  [bodyA, bodyB, a.index1, a.world0, a.generation, b.index1, b.world0,
   *   b.generation, px, py, pz, nx, ny, nz, approachSpeed]. Returns total
   *  accumulated event count and drains the shared hit queue. */
  drainContactHitEventsWithShapesInto(out) {
    this.assertLive();
    this.assertShapeIdentityAvailable();
    const q = this.contactHitQueue;
    const total = q.length / 15;
    const cap = Math.floor(out.length / 15);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      const src = i * 15;
      for (let j = 0; j < 15; j++) out[src + j] = q[src + j];
    }
    this.contactHitQueue = [];
    return total;
  }
  /** Writes [sensor, other] tuples into `out`. Returns total accumulated count.
   *  Drains the shared queue. */
  drainSensorEventsInto(out) {
    this.assertLive();
    const q = this.sensorQueue;
    const total = q.length / 2;
    const cap = Math.floor(out.length / 2);
    const write = Math.min(total, cap);
    for (let i = 0; i < write; i++) {
      out[i * 2] = q[i * 2];
      out[i * 2 + 1] = q[i * 2 + 1];
    }
    this.sensorQueue = [];
    return total;
  }
  // --- sleep control ---
  wakeBody(body) {
    this.assertLive();
    this.mod.exports.b3bridge_set_awake(body, 1);
  }
  sleepBody(body) {
    this.assertLive();
    this.mod.exports.b3bridge_set_awake(body, 0);
  }
  setAwake(body, awake) {
    this.assertLive();
    this.mod.exports.b3bridge_set_awake(body, boolInt(awake));
  }
  // --- counters ---
  awakeBodyCount() {
    this.assertLive();
    return this.mod.exports.b3bridge_get_awake_body_count(this.handle);
  }
  bodyCount() {
    this.assertLive();
    return this.mod.exports.b3bridge_get_body_count(this.handle);
  }
  // --- bulk read ---
  readTransforms(ids, out) {
    this.assertLive();
    if (!(ids instanceof Int32Array)) {
      throw new TypeError("box3d-web: readTransforms `ids` must be an Int32Array.");
    }
    if (!(out instanceof Float32Array)) {
      throw new TypeError("box3d-web: readTransforms `out` must be a Float32Array.");
    }
    if (out.length < ids.length * 7) {
      throw new RangeError("box3d-web: readTransforms `out` must have \u2265 ids.length*7 floats.");
    }
    const heap32 = this.mod.HEAP32;
    let idsPtr;
    if (ids.buffer === heap32.buffer) {
      idsPtr = ids.byteOffset;
    } else {
      idsPtr = this.idsScratch.ensure(ids.length * 4);
      this.mod.HEAP32.set(ids, idsPtr >> 2);
    }
    const heapF32 = this.mod.HEAPF32;
    const writesDirect = out.buffer === heapF32.buffer;
    const outPtr = writesDirect ? out.byteOffset : this.transformsScratch.ensure(ids.length * 7 * 4);
    this.mod.exports.b3bridge_read_transforms(idsPtr, ids.length, outPtr);
    if (!writesDirect) {
      const src = this.mod.HEAPF32;
      out.set(src.subarray(outPtr >> 2, (outPtr >> 2) + ids.length * 7));
    }
    return out;
  }
  /** Writes one byte per body (`1` awake, `0` asleep) into `out`. Reuses both
   * caller-provided buffers and returns `out`; no per-body or per-call
   * allocations are performed. */
  readSleepStates(ids, out) {
    this.assertLive();
    if (!(ids instanceof Int32Array)) {
      throw new TypeError("box3d-web: readSleepStates `ids` must be an Int32Array.");
    }
    if (!(out instanceof Uint8Array)) {
      throw new TypeError("box3d-web: readSleepStates `out` must be a Uint8Array.");
    }
    if (out.length < ids.length) {
      throw new RangeError("box3d-web: readSleepStates `out` must have \u2265 ids.length bytes.");
    }
    for (let i = 0; i < ids.length; i++) {
      out[i] = this.mod.exports.b3bridge_isBodyAwake(ids[i]) !== 0 ? 1 : 0;
    }
    return out;
  }
  destroy() {
    if (this.disposed) return;
    this.mod.exports.b3bridge_destroy_world(this.handle);
    this.idsScratch.dispose();
    this.transformsScratch.dispose();
    this.vectorScratch.dispose();
    this.eventsScratch.dispose();
    this.overlapScratch.dispose();
    this.contactQueue = [];
    this.contactEndQueue = [];
    this.contactHitQueue = [];
    this.sensorQueue = [];
    this.pointJointMetadata.clear();
    this.disposed = true;
  }
};

// src/limits.ts
var MAX_NATIVE_SHAPE_INDEX = 1 << 22;
var MAX_NATIVE_WORLDS = 128;
var MAX_NATIVE_GENERATION = 65535;

// src/helpers/fixed-step.ts
var FixedStepper = class {
  fixedDt;
  substeps;
  maxDeltaClamp;
  /** Mutable catch-up budget; change via setMaxStepsPerFrame (does not reset clocks). */
  maxStepsPerFrame;
  accumulator = 0;
  _simTime = 0;
  totalExecutedSteps = 0;
  droppedBacklogTime = 0;
  droppedBacklogSteps = 0;
  constructor(options = {}) {
    this.fixedDt = options.fixedDt && options.fixedDt > 0 ? options.fixedDt : 1 / 60;
    this.substeps = options.substeps && options.substeps > 0 ? options.substeps | 0 : 4;
    this.maxDeltaClamp = options.maxDeltaClamp && options.maxDeltaClamp > 0 ? options.maxDeltaClamp : 0.1;
    this.maxStepsPerFrame = options.maxStepsPerFrame && options.maxStepsPerFrame > 0 ? options.maxStepsPerFrame | 0 : 3;
  }
  /**
   * Update the per-frame catch-up step budget without touching accumulator or simTime.
   *
   * Finite values are truncated toward zero with `| 0` (same as the constructor).
   * The truncated result must be an integer ≥ 1; otherwise throws RangeError.
   * Fractional inputs like 2.9 become 2; 0.9 / NaN / ±Infinity / negatives throw.
   */
  setMaxStepsPerFrame(value) {
    if (!Number.isFinite(value)) {
      throw new RangeError(
        `FixedStepper.setMaxStepsPerFrame: expected finite number \u2265 1, got ${value}`
      );
    }
    const budget = value | 0;
    if (budget < 1) {
      throw new RangeError(
        `FixedStepper.setMaxStepsPerFrame: expected integer budget \u2265 1 (after truncating toward zero), got ${value}`
      );
    }
    this.maxStepsPerFrame = budget;
  }
  /**
   * Execute exactly one fixed step without consuming or changing the frame
   * accumulator. This is useful when a caller owns a shared accumulator/world
   * and needs each participant's logical clock to advance with that world.
   */
  stepOnce(onStep) {
    this.executeStep(onStep);
  }
  /**
   * Feed a frame delta; runs 0..maxStepsPerFrame fixed steps via `onStep`.
   * Returns how many steps ran (0 = no visual change needed this frame).
   */
  advance(delta, onStep) {
    const clamped = Math.min(Math.max(Number.isFinite(delta) ? delta : 0, 0), this.maxDeltaClamp);
    this.accumulator = Math.min(
      this.accumulator + clamped,
      this.maxDeltaClamp + this.fixedDt
    );
    let steps = 0;
    while (this.accumulator >= this.fixedDt && steps < this.maxStepsPerFrame) {
      this.executeStep(onStep);
      this.accumulator -= this.fixedDt;
      steps += 1;
    }
    if (this.accumulator >= this.fixedDt) {
      const retained = this.accumulator % this.fixedDt;
      const dropped = this.accumulator - retained;
      this.accumulator = retained;
      this.droppedBacklogTime += dropped;
      this.droppedBacklogSteps += Math.max(0, Math.round(dropped / this.fixedDt));
    }
    return steps;
  }
  /** Snapshot counters without exposing mutable internal state. */
  telemetry() {
    return Object.freeze({
      simTime: this._simTime,
      accumulator: this.accumulator,
      totalExecutedSteps: this.totalExecutedSteps,
      droppedBacklogTime: this.droppedBacklogTime,
      droppedBacklogSteps: this.droppedBacklogSteps,
      maxStepsPerFrame: this.maxStepsPerFrame
    });
  }
  executeStep(onStep) {
    onStep(this.fixedDt);
    this._simTime += this.fixedDt;
    this.totalExecutedSteps += 1;
  }
  /** Total simulated time (s), monotone in fixed increments. */
  get simTime() {
    return this._simTime;
  }
  reset() {
    this.accumulator = 0;
    this._simTime = 0;
    this.totalExecutedSteps = 0;
    this.droppedBacklogTime = 0;
    this.droppedBacklogSteps = 0;
  }
};

// src/helpers/transform-buffer.ts
var TransformBuffer = class {
  // Insertion-ordered live body list (the source of truth for order).
  order = [];
  dirty = true;
  _ids;
  _transforms;
  _count = 0;
  // body -> current slot index, rebuilt on demand.
  offsets = /* @__PURE__ */ new Map();
  constructor(capacity = 0) {
    const cap = Math.max(0, capacity | 0);
    this._ids = new Int32Array(cap);
    this._transforms = new Float32Array(cap * 7);
  }
  /** Track a body (appended in insertion order). Marks dirty. */
  add(body) {
    this.order.push(body);
    this.dirty = true;
  }
  /** Untrack a body. Marks dirty; packed arrays compact (later bodies renumber)
   *  on the next rebuild. */
  remove(body) {
    const idx = this.order.indexOf(body);
    if (idx !== -1) {
      this.order.splice(idx, 1);
      this.dirty = true;
    }
  }
  markDirty() {
    this.dirty = true;
  }
  /** Rebuild the packed id array if dirty (call before readInto). */
  rebuild() {
    if (!this.dirty) return;
    const n = this.order.length;
    if (this._ids.length < n) {
      this._ids = new Int32Array(n);
    }
    if (this._transforms.length < n * 7) {
      this._transforms = new Float32Array(n * 7);
    }
    this.offsets.clear();
    for (let i = 0; i < n; i++) {
      const body = this.order[i];
      this._ids[i] = body;
      this.offsets.set(body, i * 7);
    }
    this._count = n;
    this.dirty = false;
  }
  /** Read all tracked bodies' poses via the world, once per step. */
  readInto(world) {
    if (this.dirty) this.rebuild();
    if (this._count === 0) return;
    const ids = this._count === this._ids.length ? this._ids : this._ids.subarray(0, this._count);
    const out = this._count * 7 === this._transforms.length ? this._transforms : this._transforms.subarray(0, this._count * 7);
    world.readTransforms(ids, out);
  }
  /** Body → CURRENT 7-float offset into `transforms` (undefined if untracked).
   *  Do not cache the result across rebuilds. */
  offsetOf(body) {
    if (this.dirty) this.rebuild();
    return this.offsets.get(body);
  }
  /** Packed, insertion-ordered ids, length = count. */
  get ids() {
    if (this.dirty) this.rebuild();
    return this._count === this._ids.length ? this._ids : this._ids.subarray(0, this._count);
  }
  /** 7*count floats. */
  get transforms() {
    if (this.dirty) this.rebuild();
    return this._count * 7 === this._transforms.length ? this._transforms : this._transforms.subarray(0, this._count * 7);
  }
  get count() {
    if (this.dirty) this.rebuild();
    return this._count;
  }
};

// src/helpers/sleep-manager.ts
var SAMPLE_STRIDE = 7;
var SleepManager = class {
  world;
  settleSteps;
  sweepIntervalSec;
  moveThresholdSq;
  angleThreshold;
  // 0 = ignore rotation
  neighborRadius;
  // 0 = per-body (legacy)
  neighborRadiusSq;
  shouldSleep;
  watched = [];
  buffer = null;
  // Force-sleep bookkeeping.
  settleCountdown = -1;
  // -1 = not pending
  // Sweep bookkeeping: last sampled pose (7 floats per watched body).
  lastSample = new Float32Array(0);
  sampleValid = false;
  nextSweepTime = 0;
  // Cluster-gate scratch (sized in watch(); unused when neighborRadius is 0).
  watchedIds = new Int32Array(0);
  sleepOut = new Uint8Array(0);
  ufParent = new Int32Array(0);
  quiet = new Uint8Array(0);
  eligible = new Uint8Array(0);
  clusterQuiet = new Uint8Array(0);
  offsets = new Int32Array(0);
  cells = /* @__PURE__ */ new Map();
  constructor(world, options = {}) {
    this.world = world;
    this.settleSteps = options.settleSteps != null && options.settleSteps >= 0 ? options.settleSteps | 0 : 2;
    this.sweepIntervalSec = options.sweepIntervalSec && options.sweepIntervalSec > 0 ? options.sweepIntervalSec : 2;
    const thr = options.moveThreshold && options.moveThreshold > 0 ? options.moveThreshold : 0.01;
    this.moveThresholdSq = thr * thr;
    this.angleThreshold = options.angleThreshold != null && options.angleThreshold > 0 ? options.angleThreshold : 0;
    this.neighborRadius = options.neighborRadius != null && options.neighborRadius > 0 ? options.neighborRadius : 0;
    this.neighborRadiusSq = this.neighborRadius * this.neighborRadius;
    this.shouldSleep = options.shouldSleep ?? (() => true);
  }
  /** Track bodies (their poses live in the given TransformBuffer). Arms a fresh
   *  force-sleep countdown and resets the sweep sampler. */
  watch(bodies, buffer) {
    this.watched = bodies.slice();
    this.buffer = buffer;
    this.settleCountdown = this.settleSteps;
    const n = this.watched.length;
    this.lastSample = new Float32Array(n * SAMPLE_STRIDE);
    this.watchedIds = new Int32Array(n);
    this.sleepOut = new Uint8Array(n);
    this.ufParent = new Int32Array(n);
    this.quiet = new Uint8Array(n);
    this.eligible = new Uint8Array(n);
    this.clusterQuiet = new Uint8Array(n);
    this.offsets = new Int32Array(n);
    for (let i = 0; i < n; i++) this.watchedIds[i] = this.watched[i];
    this.sampleValid = false;
    this.nextSweepTime = 0;
  }
  /** Force-sleep freshly spawned tracked bodies after `settleSteps` calls. Call
   *  once per fixed step; it counts down and fires exactly once. */
  forceSleepSettled() {
    if (this.settleCountdown < 0) return;
    if (this.settleCountdown > 0) {
      this.settleCountdown -= 1;
      return;
    }
    for (let i = 0; i < this.watched.length; i++) {
      const body = this.watched[i];
      if (this.shouldSleep(body)) this.world.sleepBody(body);
    }
    this.settleCountdown = -1;
  }
  /** Run the periodic <threshold sweep; no-op until the interval elapses. */
  sweep(simTime) {
    const buffer = this.buffer;
    if (!buffer || this.watched.length === 0) return;
    if (!this.sampleValid) {
      this.sample(buffer);
      this.sampleValid = true;
      this.nextSweepTime = simTime + this.sweepIntervalSec;
      return;
    }
    if (simTime < this.nextSweepTime) return;
    if (this.neighborRadius > 0) {
      this.sweepClustered(buffer);
    } else {
      this.sweepPerBody(buffer);
    }
    this.sample(buffer);
    this.nextSweepTime = simTime + this.sweepIntervalSec;
  }
  sweepPerBody(buffer) {
    const transforms = buffer.transforms;
    for (let i = 0; i < this.watched.length; i++) {
      const body = this.watched[i];
      const offset = buffer.offsetOf(body);
      if (offset === void 0) continue;
      if (this.isQuiet(i, offset, transforms) && this.shouldSleep(body)) {
        this.world.sleepBody(body);
      }
    }
  }
  sweepClustered(buffer) {
    const readSleep = this.world.readSleepStates;
    if (typeof readSleep !== "function") {
      throw new TypeError("SleepManager: neighborRadius requires world.readSleepStates");
    }
    const n = this.watched.length;
    const transforms = buffer.transforms;
    const ids = this.watchedIds;
    const sleepOut = this.sleepOut;
    const quiet = this.quiet;
    const eligible = this.eligible;
    const parent = this.ufParent;
    const offsets = this.offsets;
    readSleep.call(this.world, ids, sleepOut);
    eligible.fill(0);
    quiet.fill(0);
    for (let i = 0; i < n; i++) {
      parent[i] = i;
      const offset = buffer.offsetOf(this.watched[i]);
      offsets[i] = offset === void 0 ? -1 : offset;
      if (sleepOut[i] !== 1) continue;
      if (offset === void 0) continue;
      eligible[i] = 1;
      quiet[i] = this.isQuiet(i, offset, transforms) ? 1 : 0;
    }
    const cells = this.cells;
    cells.clear();
    const inv = 1 / this.neighborRadius;
    const last = this.lastSample;
    for (let i = 0; i < n; i++) {
      if (!eligible[i]) continue;
      const offset = offsets[i];
      this.insertCell(
        Math.floor(transforms[offset] * inv),
        Math.floor(transforms[offset + 1] * inv),
        Math.floor(transforms[offset + 2] * inv),
        i
      );
      const so = i * SAMPLE_STRIDE;
      const px = last[so];
      if (!Number.isFinite(px)) continue;
      const py = last[so + 1];
      const pz = last[so + 2];
      if (!Number.isFinite(py) || !Number.isFinite(pz)) continue;
      this.insertCell(Math.floor(px * inv), Math.floor(py * inv), Math.floor(pz * inv), i);
    }
    const radiusSq = this.neighborRadiusSq;
    for (let i = 0; i < n; i++) {
      if (!eligible[i]) continue;
      const offset = offsets[i];
      const x = transforms[offset];
      const y = transforms[offset + 1];
      const z = transforms[offset + 2];
      const so = i * SAMPLE_STRIDE;
      const px = last[so];
      const py = last[so + 1];
      const pz = last[so + 2];
      this.unionNear(i, x, y, z, inv, radiusSq, offsets, transforms, last);
      if (Number.isFinite(px) && Number.isFinite(py) && Number.isFinite(pz)) {
        this.unionNear(i, px, py, pz, inv, radiusSq, offsets, transforms, last);
      }
    }
    const clusterQuiet = this.clusterQuiet;
    clusterQuiet.fill(1);
    for (let i = 0; i < n; i++) {
      if (!eligible[i]) continue;
      if (!quiet[i]) clusterQuiet[this.ufFind(i)] = 0;
    }
    for (let i = 0; i < n; i++) {
      if (!eligible[i]) continue;
      const body = this.watched[i];
      if (clusterQuiet[this.ufFind(i)] && this.shouldSleep(body)) {
        this.world.sleepBody(body);
      }
    }
  }
  /** Quiet = displacement under moveThreshold AND (if enabled) quat under angleThreshold.
   *  Missing/NaN samples are non-quiet. Caller must skip undefined offsets. */
  isQuiet(i, offset, transforms) {
    const so = i * SAMPLE_STRIDE;
    const px = this.lastSample[so];
    const py = this.lastSample[so + 1];
    const pz = this.lastSample[so + 2];
    if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) return false;
    const cx = transforms[offset];
    const cy = transforms[offset + 1];
    const cz = transforms[offset + 2];
    if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(cz)) return false;
    const dx = cx - px;
    const dy = cy - py;
    const dz = cz - pz;
    if (dx * dx + dy * dy + dz * dz >= this.moveThresholdSq) return false;
    if (this.angleThreshold > 0) {
      const qx = this.lastSample[so + 3];
      const qy = this.lastSample[so + 4];
      const qz = this.lastSample[so + 5];
      const qw = this.lastSample[so + 6];
      if (!Number.isFinite(qx) || !Number.isFinite(qy) || !Number.isFinite(qz) || !Number.isFinite(qw)) {
        return false;
      }
      const nx = transforms[offset + 3];
      const ny = transforms[offset + 4];
      const nz = transforms[offset + 5];
      const nw = transforms[offset + 6];
      if (!Number.isFinite(nx) || !Number.isFinite(ny) || !Number.isFinite(nz) || !Number.isFinite(nw)) {
        return false;
      }
      const quatDelta = 1 - Math.abs(qx * nx + qy * ny + qz * nz + qw * nw);
      if (quatDelta >= this.angleThreshold) return false;
    }
    return true;
  }
  insertCell(ix, iy, iz, i) {
    const key = ix * 73856093 + iy * 19349663 + iz * 83492791;
    let bucket = this.cells.get(key);
    if (!bucket) {
      bucket = [];
      this.cells.set(key, bucket);
    }
    bucket.push(i);
  }
  /** Union i with any eligible j whose current or previous center is within
   *  neighborRadius of (x,y,z). (x,y,z) is i's current or previous center. */
  unionNear(i, x, y, z, inv, radiusSq, offsets, transforms, last) {
    const ix = Math.floor(x * inv);
    const iy = Math.floor(y * inv);
    const iz = Math.floor(z * inv);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const bucket = this.cells.get(
            (ix + dx) * 73856093 + (iy + dy) * 19349663 + (iz + dz) * 83492791
          );
          if (!bucket) continue;
          for (let b = 0; b < bucket.length; b++) {
            const j = bucket[b];
            if (j <= i || !this.eligible[j]) continue;
            const jo = offsets[j];
            const jx = transforms[jo];
            const jy = transforms[jo + 1];
            const jz = transforms[jo + 2];
            if (this.distSq(x, y, z, jx, jy, jz) <= radiusSq) {
              this.ufUnion(i, j);
              continue;
            }
            const js = j * SAMPLE_STRIDE;
            const jpx = last[js];
            const jpy = last[js + 1];
            const jpz = last[js + 2];
            if (Number.isFinite(jpx) && Number.isFinite(jpy) && Number.isFinite(jpz) && this.distSq(x, y, z, jpx, jpy, jpz) <= radiusSq) {
              this.ufUnion(i, j);
            }
          }
        }
      }
    }
  }
  distSq(ax, ay, az, bx, by, bz) {
    const dx = ax - bx;
    const dy = ay - by;
    const dz = az - bz;
    return dx * dx + dy * dy + dz * dz;
  }
  ufFind(i) {
    const p = this.ufParent;
    let r = i;
    while (p[r] !== r) r = p[r];
    let x = i;
    while (x !== r) {
      const n = p[x];
      p[x] = r;
      x = n;
    }
    return r;
  }
  ufUnion(a, b) {
    a = this.ufFind(a);
    b = this.ufFind(b);
    if (a !== b) this.ufParent[a] = b;
  }
  sample(buffer) {
    const transforms = buffer.transforms;
    const last = this.lastSample;
    for (let i = 0; i < this.watched.length; i++) {
      const offset = buffer.offsetOf(this.watched[i]);
      const so = i * SAMPLE_STRIDE;
      if (offset === void 0) {
        last[so] = NaN;
        last[so + 1] = NaN;
        last[so + 2] = NaN;
        last[so + 3] = NaN;
        last[so + 4] = NaN;
        last[so + 5] = NaN;
        last[so + 6] = NaN;
        continue;
      }
      last[so] = transforms[offset];
      last[so + 1] = transforms[offset + 1];
      last[so + 2] = transforms[offset + 2];
      last[so + 3] = transforms[offset + 3];
      last[so + 4] = transforms[offset + 4];
      last[so + 5] = transforms[offset + 5];
      last[so + 6] = transforms[offset + 6];
    }
  }
};

// src/helpers/radial-impulse.ts
var impulseScratch = [0, 0, 0];
var atScratch = [0, 0, 0];
function radialImpulse(world, bodies, buffer, options) {
  const { center, radius, strength } = options;
  if (!(radius > 0)) return;
  const quadratic = (options.falloff ?? "quadratic") === "quadratic";
  const upwardBias = options.upwardBias ?? 0;
  const cx = center[0];
  const cy = center[1];
  const cz = center[2];
  const transforms = buffer.transforms;
  for (let i = 0; i < bodies.length; i++) {
    const body = bodies[i];
    const offset = buffer.offsetOf(body);
    if (offset === void 0) continue;
    const bx = transforms[offset];
    const by = transforms[offset + 1];
    const bz = transforms[offset + 2];
    const dx = bx - cx;
    const dy = by - cy;
    const dz = bz - cz;
    const d = Math.hypot(dx, dy, dz);
    if (d > radius) continue;
    const f = 1 - d / radius;
    const s = quadratic ? strength * f * f : strength * f;
    const inv = d > 1e-3 ? 1 / d : 0;
    impulseScratch[0] = dx * inv * s;
    impulseScratch[1] = dy * inv * s + upwardBias * s;
    impulseScratch[2] = dz * inv * s;
    atScratch[0] = bx;
    atScratch[1] = by;
    atScratch[2] = bz;
    world.wakeBody(body);
    world.applyImpulse(body, impulseScratch, atScratch);
  }
}

// src/helpers/body-pool.ts
var BodyPool = class {
  world;
  max;
  maxAgeSec;
  onEvict;
  _bodies = [];
  bornAt = /* @__PURE__ */ new Map();
  simTime = 0;
  constructor(world, options) {
    this.world = world;
    this.max = Math.max(0, options.max | 0);
    this.maxAgeSec = options.maxAgeSec != null && Number.isFinite(options.maxAgeSec) && options.maxAgeSec > 0 ? options.maxAgeSec : void 0;
    this.onEvict = options.onEvict;
  }
  /** Spawn via your factory, register, and evict oldest if over cap. */
  spawn(create, simTime = this.simTime) {
    const body = create(this.world);
    this._bodies.push(body);
    this.bornAt.set(body, simTime);
    this.simTime = simTime;
    while (this._bodies.length > this.max) {
      const oldest = this._bodies.shift();
      if (oldest !== void 0) {
        this.bornAt.delete(oldest);
        this.dispose(oldest);
      }
    }
    return body;
  }
  /** Retire and destroy a tracked body. Returns false when it is already retired. */
  retire(body) {
    const index = this._bodies.indexOf(body);
    if (index < 0) return false;
    this._bodies.splice(index, 1);
    this.bornAt.delete(body);
    this.dispose(body);
    return true;
  }
  /** Retire bodies whose age has reached `maxAgeSec`; no-op when age expiry is disabled. */
  sweep(simTime) {
    const maxAgeSec = this.maxAgeSec;
    if (maxAgeSec === void 0 || !Number.isFinite(simTime)) return 0;
    this.simTime = simTime;
    const expired = this._bodies.filter((body) => {
      const born = this.bornAt.get(body);
      return born !== void 0 && simTime - born >= maxAgeSec;
    });
    let retired = 0;
    for (const body of expired) {
      if (this.retire(body)) retired += 1;
    }
    return retired;
  }
  has(body) {
    return this._bodies.includes(body);
  }
  destroyAll() {
    const bodies = this._bodies.splice(0);
    let firstError;
    let failed = false;
    for (const body of bodies) {
      this.bornAt.delete(body);
      try {
        this.dispose(body);
      } catch (error) {
        if (!failed) {
          firstError = error;
          failed = true;
        }
      }
    }
    if (failed) throw firstError;
  }
  get bodies() {
    return this._bodies;
  }
  dispose(body) {
    try {
      this.onEvict?.(body);
    } finally {
      this.world.destroyBody(body);
    }
  }
};

// src/helpers/articulated-pose.ts
var ArticulatedPoseError = class extends Error {
  name = "ArticulatedPoseError";
  measurements;
  maxJointAnchorSeparation;
  constructor(message, measurements, maxJointAnchorSeparation) {
    super(message);
    this.measurements = measurements;
    this.maxJointAnchorSeparation = maxJointAnchorSeparation;
  }
};
function finiteTuple(values, length) {
  return values.length === length && values.every((value) => Number.isFinite(value));
}
function validatePoseInput(poses) {
  const seen = /* @__PURE__ */ new Set();
  for (const pose of poses) {
    if (!pose || !Number.isInteger(pose.body) || pose.body <= 0) {
      throw new TypeError("box3d-web: articulated pose body handles must be positive integers.");
    }
    if (seen.has(pose.body)) {
      throw new RangeError(`box3d-web: articulated pose contains duplicate body ${pose.body}.`);
    }
    seen.add(pose.body);
    if (!finiteTuple(pose.position, 3) || !finiteTuple(pose.rotation, 4)) {
      throw new TypeError("box3d-web: articulated pose transforms must contain finite tuples.");
    }
    const [qx, qy, qz, qw] = pose.rotation;
    if (qx * qx + qy * qy + qz * qz + qw * qw <= 1e-12) {
      throw new RangeError("box3d-web: articulated pose rotations must be non-zero quaternions.");
    }
  }
}
function validateJoints(joints) {
  const seen = /* @__PURE__ */ new Set();
  for (const joint of joints) {
    if (!Number.isInteger(joint) || joint <= 0) {
      throw new TypeError("box3d-web: articulated pose joint handles must be positive integers.");
    }
    if (seen.has(joint)) {
      throw new RangeError(`box3d-web: articulated pose contains duplicate joint ${joint}.`);
    }
    seen.add(joint);
  }
}
function applyArticulatedPose(world, poses, options = {}) {
  if (!Array.isArray(poses)) {
    throw new TypeError("box3d-web: articulated poses must be an array.");
  }
  validatePoseInput(poses);
  const joints = options.joints ?? [];
  if (!Array.isArray(joints)) {
    throw new TypeError("box3d-web: articulated pose joints must be an array.");
  }
  validateJoints(joints);
  const maxAllowed = options.maxJointAnchorSeparation ?? 1e-3;
  if (!Number.isFinite(maxAllowed) || maxAllowed < 0) {
    throw new RangeError("box3d-web: maxJointAnchorSeparation must be finite and non-negative.");
  }
  for (const joint of joints) world.getJointAnchorSeparation(joint);
  const ids = new Int32Array(poses.map((pose) => pose.body));
  const originalTransforms = new Float32Array(poses.length * 7);
  world.readTransforms(ids, originalTransforms);
  const originalAwake = poses.map((pose) => world.isBodyAwake(pose.body));
  for (let i = 0; i < poses.length; i++) {
    const offset = i * 7;
    if (!Number.isFinite(originalTransforms[offset]) || !Number.isFinite(originalTransforms[offset + 1]) || !Number.isFinite(originalTransforms[offset + 2])) {
      throw new RangeError(`box3d-web: articulated pose body ${poses[i].body} is not live.`);
    }
  }
  const rollback = () => {
    for (let i = 0; i < poses.length; i++) {
      const offset = i * 7;
      world.setBodyTransform(
        poses[i].body,
        [originalTransforms[offset], originalTransforms[offset + 1], originalTransforms[offset + 2]],
        [
          originalTransforms[offset + 3],
          originalTransforms[offset + 4],
          originalTransforms[offset + 5],
          originalTransforms[offset + 6]
        ]
      );
    }
    for (let i = 0; i < poses.length; i++) {
      world.setAwake(poses[i].body, originalAwake[i]);
    }
  };
  try {
    for (const pose of poses) world.setBodyTransform(pose.body, pose.position, pose.rotation);
    const separations = joints.map((joint) => ({
      joint,
      separation: world.getJointAnchorSeparation(joint)
    }));
    const maxSeparation = separations.reduce(
      (max, measurement) => Math.max(max, measurement.separation),
      0
    );
    if (maxSeparation > maxAllowed) {
      rollback();
      throw new ArticulatedPoseError(
        `box3d-web: articulated pose exceeds max joint-anchor separation (${maxSeparation} > ${maxAllowed}).`,
        separations,
        maxAllowed
      );
    }
    if (options.clearVelocities ?? true) {
      for (const pose of poses) {
        world.setLinearVelocity(pose.body, [0, 0, 0]);
        world.setAngularVelocity(pose.body, [0, 0, 0]);
      }
    }
    if (options.sleep) {
      for (const pose of poses) world.sleepBody(pose.body);
    }
    return { separations, maxSeparation };
  } catch (error) {
    if (error instanceof ArticulatedPoseError) throw error;
    rollback();
    throw error;
  }
}

// src/helpers/identity.ts
function checkedNamespace(namespace) {
  if (typeof namespace !== "string" || namespace.trim().length === 0) {
    throw new TypeError("identity namespace must be a non-empty string");
  }
  return namespace;
}
function checkedGeneration(generation) {
  if (!Number.isSafeInteger(generation) || generation < 0) {
    throw new RangeError("identity generation must be a non-negative safe integer");
  }
  return generation;
}
function checkedOrdinal(ordinal) {
  if (!Number.isSafeInteger(ordinal) || ordinal < 0) {
    throw new RangeError("identity ordinal must be a non-negative safe integer");
  }
  return ordinal;
}
function acceptedParentKinds(spec) {
  if (spec === null) return null;
  if (typeof spec === "string") return [spec];
  return spec;
}
function parentRequirementMessage(kind, accepted) {
  if (accepted.length === 1) {
    return `${kind} identity requires a ${accepted[0]} parent`;
  }
  if (accepted.length === 2) {
    return `${kind} identity requires a ${accepted[0]} or ${accepted[1]} parent`;
  }
  const head = accepted.slice(0, -1).join(", ");
  const last = accepted[accepted.length - 1] ?? "";
  return `${kind} identity requires a ${head}, or ${last} parent`;
}
function sameIdentity(a, b) {
  return a !== void 0 && b !== void 0 && a.key === b.key;
}
function createIdentitySystem(config) {
  const kindSet = new Set(config.kinds);
  const parentOf = config.parentOf;
  function mintIdentity(kind, namespace, generation, ordinal, parent) {
    const checked = checkedOrdinal(ordinal);
    if (!kindSet.has(kind)) {
      throw new Error(`${kind} is not a registered identity kind`);
    }
    if (parent && (parent.namespace !== namespace || parent.generation !== generation)) {
      throw new Error("identity parent must share namespace and generation");
    }
    const spec = parentOf[kind];
    const accepted = acceptedParentKinds(spec);
    if (accepted === null) {
      if (parent) throw new Error(`${kind} identity cannot have a parent`);
    } else if (!parent || !accepted.includes(parent.kind)) {
      throw new Error(parentRequirementMessage(kind, accepted));
    }
    const parentSuffix = parent ? `/${parent.key}` : "";
    const encodedNamespace = encodeURIComponent(namespace);
    return Object.freeze({
      kind,
      namespace,
      generation,
      ordinal: checked,
      key: `${kind}:${encodedNamespace}:g${generation}:n${checked}${parentSuffix}`,
      ...parent ? { parent } : {}
    });
  }
  return {
    forScope(input) {
      const namespace = checkedNamespace(input.namespace);
      const generation = checkedGeneration(input.generation);
      const scope = Object.freeze({ namespace, generation });
      return {
        scope,
        mint(kind, ordinal, parent) {
          return mintIdentity(kind, namespace, generation, ordinal, parent);
        }
      };
    },
    sameIdentity
  };
}

// src/helpers/entity-registry.ts
function checkedHandle(value, label) {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive safe integer handle`);
  }
  return value;
}
function assertObject(value, label) {
  if (value === null || typeof value !== "object") {
    throw new TypeError(`${label} must be an object`);
  }
}
function freezeAddress(address) {
  if (address === null || typeof address !== "object") return address;
  return Object.freeze({ ...address });
}
function checkedShapeIdentity(value) {
  assertObject(value, "shapeIdentity");
  if (!Number.isSafeInteger(value.index1) || value.index1 <= 0 || value.index1 > MAX_NATIVE_SHAPE_INDEX) {
    throw new RangeError(
      `shapeIdentity.index1 must be in the native range 1..${MAX_NATIVE_SHAPE_INDEX}`
    );
  }
  if (!Number.isSafeInteger(value.world0) || value.world0 < 0 || value.world0 >= MAX_NATIVE_WORLDS) {
    throw new RangeError(
      `shapeIdentity.world0 must be in the native world range 0..${MAX_NATIVE_WORLDS - 1}`
    );
  }
  if (!Number.isSafeInteger(value.generation) || value.generation < 0 || value.generation > MAX_NATIVE_GENERATION) {
    throw new RangeError(
      `shapeIdentity.generation must be in the uint16 range 0..${MAX_NATIVE_GENERATION}`
    );
  }
  return Object.freeze({
    index1: value.index1,
    world0: value.world0,
    generation: value.generation
  });
}
function shapeKey(identity) {
  return `${identity.world0}:${identity.index1}:g${identity.generation}`;
}
function compareBodies(left, right) {
  return left.body - right.body;
}
function compareShapes(left, right) {
  const leftIdentity = left.identity;
  const rightIdentity = right.identity;
  return leftIdentity.world0 - rightIdentity.world0 || leftIdentity.index1 - rightIdentity.index1 || leftIdentity.generation - rightIdentity.generation || left.shape - right.shape;
}
function checkedKey(value, label) {
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(`${label} must return a non-empty string`);
  }
  return value;
}
var EntityRegistry = class {
  activeWorld;
  activeShapeWorld0;
  addressKeyOf;
  groupKeyOf;
  addressWorldKeyOf;
  bodies = /* @__PURE__ */ new Map();
  bodyKeys = /* @__PURE__ */ new Map();
  shapes = /* @__PURE__ */ new Map();
  shapeHandles = /* @__PURE__ */ new Map();
  bodyShapes = /* @__PURE__ */ new Map();
  groupBodies = /* @__PURE__ */ new Map();
  constructor(options) {
    if (options === null || typeof options !== "object") {
      throw new TypeError("entity registry options must be an object");
    }
    if (typeof options.addressKey !== "function") {
      throw new TypeError("entity registry addressKey must be a function");
    }
    if (options.groupKey !== void 0 && typeof options.groupKey !== "function") {
      throw new TypeError("entity registry groupKey must be a function when supplied");
    }
    if (options.addressWorldKey !== void 0 && typeof options.addressWorldKey !== "function") {
      throw new TypeError(
        "entity registry addressWorldKey must be a function when supplied"
      );
    }
    if (options.worldKey !== void 0) {
      if (typeof options.worldKey !== "string" || options.worldKey.length === 0) {
        throw new TypeError(
          "entity registry worldKey must be a non-empty string when supplied"
        );
      }
      this.activeWorld = options.worldKey;
    }
    this.addressKeyOf = options.addressKey;
    this.groupKeyOf = options.groupKey;
    this.addressWorldKeyOf = options.addressWorldKey;
  }
  registerBody(body, address) {
    const bodyNumber = checkedHandle(body, "body");
    const validatedAddress = freezeAddress(address);
    const key = checkedKey(
      this.addressKeyOf(validatedAddress),
      "entity registry addressKey"
    );
    const group = this.resolveGroupKey(validatedAddress);
    const world = this.resolveAddressWorld(validatedAddress);
    if (world !== void 0) this.assertWorld(world);
    if (this.bodies.has(bodyNumber)) {
      throw new Error(`entity registry: body ${bodyNumber} is already registered`);
    }
    if (this.bodyKeys.has(key)) {
      throw new Error(`entity registry: address ${key} is already registered`);
    }
    const registration = Object.freeze({
      body,
      address: validatedAddress
    });
    this.bodies.set(bodyNumber, registration);
    this.bodyKeys.set(key, bodyNumber);
    this.bodyShapes.set(bodyNumber, /* @__PURE__ */ new Set());
    if (group !== void 0) {
      let entries = this.groupBodies.get(group);
      if (entries === void 0) {
        entries = /* @__PURE__ */ new Set();
        this.groupBodies.set(group, entries);
      }
      entries.add(bodyNumber);
    }
    return registration;
  }
  registerShape(shape, body, identity) {
    const shapeNumber = checkedHandle(shape, "shape");
    const bodyNumber = checkedHandle(body, "body");
    const validatedIdentity = checkedShapeIdentity(identity);
    const bodyRegistration = this.bodies.get(bodyNumber);
    if (bodyRegistration === void 0) {
      throw new Error(`entity registry: body ${bodyNumber} is not registered`);
    }
    this.assertShapeWorld(validatedIdentity.world0);
    const key = shapeKey(validatedIdentity);
    if (this.shapes.has(key)) {
      throw new Error(`entity registry: shape identity ${key} is already registered`);
    }
    if (this.shapeHandles.get(shapeNumber) !== void 0) {
      throw new Error(
        `entity registry: shape handle ${shapeNumber} is already registered`
      );
    }
    const registration = Object.freeze({
      shape,
      identity: validatedIdentity,
      body,
      bodyRegistration
    });
    this.shapes.set(key, registration);
    this.shapeHandles.set(shapeNumber, key);
    this.bodyShapes.get(bodyNumber).add(key);
    return registration;
  }
  resolveBody(body) {
    return this.bodies.get(checkedHandle(body, "body"));
  }
  resolveShape(identity) {
    return this.shapes.get(shapeKey(checkedShapeIdentity(identity)));
  }
  unregisterShape(identity) {
    const key = shapeKey(checkedShapeIdentity(identity));
    const registration = this.shapes.get(key);
    if (registration === void 0) return false;
    this.shapes.delete(key);
    this.shapeHandles.delete(registration.shape);
    this.bodyShapes.get(registration.body)?.delete(key);
    return true;
  }
  unregisterBody(body) {
    const bodyNumber = checkedHandle(body, "body");
    const registration = this.bodies.get(bodyNumber);
    if (registration === void 0) return false;
    const shapes = this.bodyShapes.get(bodyNumber);
    if (shapes !== void 0) {
      for (const key of shapes) {
        const shapeRegistration = this.shapes.get(key);
        if (shapeRegistration !== void 0) {
          this.shapeHandles.delete(shapeRegistration.shape);
        }
        this.shapes.delete(key);
      }
    }
    this.bodyShapes.delete(bodyNumber);
    this.bodies.delete(bodyNumber);
    this.bodyKeys.delete(
      checkedKey(
        this.addressKeyOf(registration.address),
        "entity registry addressKey"
      )
    );
    const group = this.resolveGroupKey(registration.address);
    if (group !== void 0) {
      const entries = this.groupBodies.get(group);
      entries?.delete(bodyNumber);
      if (entries?.size === 0) this.groupBodies.delete(group);
    }
    return true;
  }
  /** Remove only bodies (and their shapes) owned by the supplied group. */
  unregisterGroup(groupKey) {
    if (typeof groupKey !== "string" || groupKey.length === 0) {
      throw new TypeError("entity registry group key must be a non-empty string");
    }
    const entries = this.groupBodies.get(groupKey);
    if (entries === void 0) return 0;
    const bodies = [...entries];
    for (const body of bodies) this.unregisterBody(body);
    return bodies.length;
  }
  clear() {
    this.bodies.clear();
    this.bodyKeys.clear();
    this.shapes.clear();
    this.shapeHandles.clear();
    this.bodyShapes.clear();
    this.groupBodies.clear();
  }
  getStats() {
    let groupBodyCount = 0;
    for (const entries of this.groupBodies.values()) groupBodyCount += entries.size;
    return Object.freeze({
      bodyCount: this.bodies.size,
      shapeCount: this.shapes.size,
      groupCount: this.groupBodies.size,
      groupBodyCount,
      ungroupedBodyCount: this.bodies.size - groupBodyCount
    });
  }
  snapshot() {
    const bodies = [...this.bodies.values()].sort(compareBodies);
    const shapes = [...this.shapes.values()].sort(compareShapes);
    return Object.freeze({
      stats: this.getStats(),
      bodies: Object.freeze(bodies),
      shapes: Object.freeze(shapes)
    });
  }
  resolveGroupKey(address) {
    if (this.groupKeyOf === void 0) return void 0;
    const group = this.groupKeyOf(address);
    if (group === void 0) return void 0;
    return checkedKey(group, "entity registry groupKey");
  }
  resolveAddressWorld(address) {
    if (this.addressWorldKeyOf === void 0) return void 0;
    return checkedKey(
      this.addressWorldKeyOf(address),
      "entity registry addressWorldKey"
    );
  }
  assertWorld(worldKey) {
    if (this.activeWorld === void 0) {
      this.activeWorld = worldKey;
      return;
    }
    if (this.activeWorld !== worldKey) {
      throw new Error("entity registry: address belongs to a different physics world");
    }
  }
  assertShapeWorld(world0) {
    if (this.activeShapeWorld0 === void 0) {
      this.activeShapeWorld0 = world0;
      return;
    }
    if (this.activeShapeWorld0 !== world0) {
      throw new Error(
        "entity registry: shape identity belongs to a different physics world"
      );
    }
  }
};
function createEntityRegistry(options) {
  return new EntityRegistry(options);
}

// src/helpers/scene-runtime.ts
function cloneIdentity(id) {
  return Object.freeze({
    index1: id.index1,
    world0: id.world0,
    generation: id.generation
  });
}
function clonePayloadValue(value) {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return Object.freeze(value.map(clonePayloadValue));
  const copy = {};
  for (const [key, nested] of Object.entries(value)) {
    copy[key] = clonePayloadValue(nested);
  }
  return Object.freeze(copy);
}
function clonePayload(payload) {
  return clonePayloadValue(payload);
}
function freezeBatch(begins, ends, hits) {
  const b = begins.map(
    (e) => Object.freeze({
      ...e,
      shapeA: cloneIdentity(e.shapeA),
      shapeB: cloneIdentity(e.shapeB)
    })
  );
  const en = ends.map(
    (e) => Object.freeze({
      ...e,
      shapeA: cloneIdentity(e.shapeA),
      shapeB: cloneIdentity(e.shapeB)
    })
  );
  const h = hits.map(
    (e) => Object.freeze({
      ...e,
      point: Object.freeze({ ...e.point }),
      normal: Object.freeze({ ...e.normal }),
      shapeA: cloneIdentity(e.shapeA),
      shapeB: cloneIdentity(e.shapeB)
    })
  );
  return Object.freeze({
    begins: Object.freeze(b),
    ends: Object.freeze(en),
    hits: Object.freeze(h)
  });
}
function checkedPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}
function checkedFixedDt(value) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(
      "scene runtime fixedDt must be a finite number greater than 0"
    );
  }
  return value;
}
function checkedSubsteps(value) {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError("scene runtime substeps must be an integer >= 1");
  }
  return value;
}
var SceneRuntime = class {
  world;
  stepper;
  commandCapacity;
  eventCapacity;
  classifyContact;
  adapters = /* @__PURE__ */ new Map();
  commands = [];
  events = [];
  sequence = 0;
  eventSequence = 0;
  fixedStepIndex = 0;
  epoch = 0;
  disposed = false;
  faulted = false;
  queued = 0;
  coalesced = 0;
  dropped = 0;
  rejected = 0;
  retained = 0;
  droppedEvents = 0;
  faults = 0;
  unroutedContacts = 0;
  constructor(options) {
    if (options === null || typeof options !== "object") {
      throw new TypeError("scene runtime options must be an object");
    }
    if (options.world === null || options.world === void 0) {
      throw new TypeError("scene runtime requires a world");
    }
    this.world = options.world;
    this.classifyContact = options.classifyContact;
    const commandCapacity = checkedPositiveInteger(
      options.commandCapacity ?? 128,
      "commandCapacity"
    );
    const eventCapacity = checkedPositiveInteger(
      options.eventCapacity ?? 1024,
      "eventCapacity"
    );
    this.commandCapacity = commandCapacity;
    this.eventCapacity = eventCapacity;
    if (options.stepper !== void 0) {
      checkedFixedDt(options.stepper.fixedDt);
      checkedSubsteps(options.stepper.substeps);
      this.stepper = options.stepper;
    } else {
      const fixedDt = checkedFixedDt(options.fixedDt ?? 1 / 60);
      const substeps = checkedSubsteps(options.substeps ?? 4);
      const maxStepsPerFrame = checkedPositiveInteger(
        options.maxStepsPerFrame ?? 3,
        "maxStepsPerFrame"
      );
      this.stepper = new FixedStepper({ fixedDt, substeps, maxStepsPerFrame });
    }
  }
  attach(adapter) {
    this.assertLive();
    if (adapter.physicsWorld !== this.world) {
      throw new Error("participant belongs to a different physics world");
    }
    if (Math.abs(adapter.fixedDt - this.stepper.fixedDt) > 1e-12 || adapter.substeps !== this.stepper.substeps) {
      throw new Error("participant timing is incompatible with scene");
    }
    if (this.adapters.has(adapter.participantKey)) {
      throw new Error(`participant ${adapter.participantKey} is already attached`);
    }
    this.adapters.set(adapter.participantKey, adapter);
    try {
      adapter.attachSceneOwner(this);
    } catch (error) {
      this.adapters.delete(adapter.participantKey);
      throw error;
    }
  }
  detach(adapterOrKey) {
    const key = typeof adapterOrKey === "string" ? adapterOrKey : adapterOrKey.participantKey;
    const adapter = this.adapters.get(key);
    if (adapter === void 0) return;
    try {
      adapter.detachSceneOwner(this);
    } finally {
      this.purgeParticipant(key);
      this.adapters.delete(key);
    }
  }
  /** Remove queued input and retained output for a participant being replaced. */
  purgeParticipant(participantKey) {
    const participant = this.adapters.get(participantKey);
    const pending = this.commands.filter(
      (command) => command.targetParticipantKey === participantKey
    );
    this.commands = this.commands.filter(
      (command) => command.targetParticipantKey !== participantKey
    );
    for (const command of pending) {
      if (this.commandPolicy(participant, command.type) === "critical") {
        this.rejected += 1;
      } else {
        this.dropped += 1;
      }
    }
    const retained = this.events.filter(
      (event) => event.participantKey === participantKey
    ).length;
    if (retained > 0) {
      this.events = this.events.filter(
        (event) => event.participantKey !== participantKey
      );
      this.retained = this.events.length;
      this.droppedEvents += retained;
    }
  }
  enqueueCommand(input) {
    this.assertHealthy();
    const adapter = this.adapters.get(input.targetParticipantKey);
    if (adapter === void 0) {
      throw new Error(`unknown participant ${input.targetParticipantKey}`);
    }
    if (this.commandPolicy(adapter, input.type) === "coalescible") {
      const existing = this.commands.find(
        (c) => c.targetParticipantKey === input.targetParticipantKey && c.type === input.type
      );
      if (existing) {
        const clonedPayload2 = clonePayload(input.payload);
        const replacement = Object.freeze({
          ...existing,
          payload: adapter.prepareSceneCommand?.(input.type, clonedPayload2) ?? clonedPayload2
        });
        this.commands[this.commands.indexOf(existing)] = replacement;
        this.coalesced += 1;
        return replacement;
      }
    }
    if (this.commands.length >= this.commandCapacity) {
      const index = this.commands.findIndex(
        (c) => this.commandPolicy(this.adapters.get(c.targetParticipantKey), c.type) === "coalescible"
      );
      if (index >= 0) {
        this.commands.splice(index, 1);
        this.dropped += 1;
      } else {
        this.rejected += 1;
        throw new RangeError("scene runtime command queue is full");
      }
    }
    const clonedPayload = clonePayload(input.payload);
    const preparedPayload = adapter.prepareSceneCommand?.(input.type, clonedPayload) ?? clonedPayload;
    const command = Object.freeze({
      sequence: ++this.sequence,
      sceneEpoch: this.epoch,
      fixedStepIndex: this.fixedStepIndex + 1,
      targetParticipantKey: input.targetParticipantKey,
      targetRevision: adapter.revision,
      type: input.type,
      payload: preparedPayload
    });
    this.commands.push(command);
    this.queued += 1;
    return command;
  }
  advance(delta) {
    this.assertHealthy();
    if (!Number.isFinite(delta) || delta < 0) {
      throw new RangeError("delta must be finite and non-negative");
    }
    let stepped = 0;
    let firstFault;
    try {
      this.stepper.advance(delta, () => {
        this.fixedStepIndex += 1;
        this.applyCommands();
        this.world.step(this.stepper.fixedDt, this.stepper.substeps);
        const batch = freezeBatch(
          this.world.drainContactBeginEventsWithShapes(),
          this.world.drainContactEndEventsWithShapes(),
          this.world.drainContactHitEventsWithShapes()
        );
        this.classifyContacts(batch);
        for (const adapter of this.sortedParticipants()) {
          try {
            adapter.sceneFixedStep(this, batch);
            this.captureEvents(adapter);
          } catch (error) {
            this.recordFault(error);
            if (firstFault === void 0) firstFault = error;
          }
        }
        stepped += 1;
      });
      for (const adapter of this.sortedParticipants()) {
        try {
          adapter.finishSceneFrame(this, delta, stepped > 0);
        } catch (error) {
          this.recordFault(error);
          if (firstFault === void 0) firstFault = error;
        }
      }
      if (firstFault !== void 0) throw firstFault;
      return stepped;
    } catch (error) {
      if (!this.faulted) this.recordFault(error);
      throw error;
    }
  }
  drainEvents() {
    const drained = this.events;
    this.events = [];
    this.retained = 0;
    return Object.freeze(drained);
  }
  /**
   * Restore a healthy runtime: clear queued commands and retained events, bump
   * the scene epoch, reset participant clocks, and run the reset barrier.
   */
  reset() {
    this.assertLive();
    this.commands = [];
    this.events = [];
    this.retained = 0;
    this.stepper.reset();
    this.fixedStepIndex = 0;
    this.epoch += 1;
    let firstError;
    for (const adapter of this.sortedParticipants()) {
      try {
        adapter.sceneReset(this);
      } catch (error) {
        this.recordFault(error);
        if (firstError === void 0) firstError = error;
      }
      try {
        adapter.sceneResetClock(this);
      } catch (error) {
        this.recordFault(error);
        if (firstError === void 0) firstError = error;
      }
    }
    try {
      this.world.drainContactBeginEventsWithShapes();
      this.world.drainContactEndEventsWithShapes();
      this.world.drainContactHitEventsWithShapes();
    } catch (error) {
      this.recordFault(error);
      if (firstError === void 0) firstError = error;
    }
    if (firstError !== void 0) throw firstError;
    this.faulted = false;
  }
  telemetry() {
    return Object.freeze({
      queued: this.queued,
      coalesced: this.coalesced,
      dropped: this.dropped,
      rejected: this.rejected,
      retained: this.retained,
      droppedEvents: this.droppedEvents,
      faults: this.faults,
      unroutedContacts: this.unroutedContacts,
      faulted: this.faulted
    });
  }
  dispose() {
    if (this.disposed) return;
    for (const adapter of [...this.adapters.values()]) this.detach(adapter);
    this.commands = [];
    this.events = [];
    this.retained = 0;
    this.disposed = true;
  }
  applyCommands() {
    const due = this.commands.filter(
      (c) => c.sceneEpoch === this.epoch && c.fixedStepIndex <= this.fixedStepIndex
    ).sort(
      (a, b) => a.fixedStepIndex - b.fixedStepIndex || a.sequence - b.sequence
    );
    this.commands = this.commands.filter((c) => !due.includes(c));
    for (const command of due) {
      const adapter = this.adapters.get(command.targetParticipantKey);
      if (!adapter) {
        if (this.commandPolicy(void 0, command.type) === "critical") {
          this.rejected += 1;
        } else {
          this.dropped += 1;
        }
        throw new Error(
          `command target ${command.targetParticipantKey} is no longer attached`
        );
      }
      if (command.targetRevision !== adapter.revision) {
        if (this.commandPolicy(adapter, command.type) === "critical") {
          this.rejected += 1;
        } else {
          this.dropped += 1;
        }
        throw new Error(
          `stale command revision for ${command.targetParticipantKey}`
        );
      }
      adapter.applySceneCommand(this, command);
    }
  }
  captureEvents(adapter) {
    for (const event of adapter.drainSceneEvents(this)) {
      if (this.events.length >= this.eventCapacity) {
        this.events.shift();
        this.droppedEvents += 1;
      }
      this.events.push(
        Object.freeze({
          sceneEpoch: this.epoch,
          fixedStep: this.fixedStepIndex,
          sequence: ++this.eventSequence,
          participantKey: adapter.participantKey,
          participantRevision: adapter.revision,
          event
        })
      );
      this.retained = this.events.length;
    }
  }
  classifyContacts(batch) {
    const classify = this.classifyContact;
    if (classify === void 0) return;
    for (const begin of batch.begins) {
      if (classify(begin) === "unrouted") this.unroutedContacts += 1;
    }
  }
  sortedParticipants() {
    return [...this.adapters.values()].sort(
      (a, b) => a.participantKey.localeCompare(b.participantKey)
    );
  }
  commandPolicy(participant, type) {
    return participant?.sceneCommandPolicy?.(type) ?? "critical";
  }
  assertLive() {
    if (this.disposed) throw new Error("scene runtime used after dispose()");
  }
  assertHealthy() {
    this.assertLive();
    if (this.faulted) throw new Error("scene runtime is faulted; call reset()");
  }
  recordFault(error) {
    this.faulted = true;
    this.faults += 1;
  }
};
function createSceneRuntime(options) {
  return new SceneRuntime(options);
}

// src/helpers/transactional-spawn.ts
function copyShapeIdentity(identity) {
  return Object.freeze({
    index1: identity.index1,
    world0: identity.world0,
    generation: identity.generation
  });
}
function shapeKey2(identity) {
  return `${identity.world0}:${identity.index1}:g${identity.generation}`;
}
function checkedHandle2(value, label) {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive safe integer handle`);
  }
  return value;
}
function addSpawnShape(world, body, spec) {
  switch (spec.kind) {
    case "box":
      return world.addBox(body, spec.half, spec.material);
    case "sphere":
      return world.addSphere(body, spec.radius, spec.material);
    case "capsule":
      return world.addCapsule(body, spec.radius, spec.halfHeight, spec.material);
    case "sensorBox":
      return world.addSensorBox(body, spec.half);
    case "hull":
      return world.addHull(body, spec.points, spec.options);
    default: {
      const kind = spec.kind;
      throw new TypeError(`transactional spawn: unsupported shape kind ${String(kind)}`);
    }
  }
}
function requireShapeIdentity(world, shape, label) {
  const identity = world.getShapeIdentity(shape);
  if (identity === null) {
    throw new Error(`${label}: Box3D returned no shape identity`);
  }
  return copyShapeIdentity(identity);
}
function spawnRegistered(world, registry, options) {
  if (world === null || typeof world !== "object") {
    throw new TypeError("transactional spawn requires a world");
  }
  if (registry === null || typeof registry !== "object") {
    throw new TypeError("transactional spawn requires an entity registry");
  }
  if (options === null || typeof options !== "object") {
    throw new TypeError("transactional spawn options must be an object");
  }
  if (!Array.isArray(options.shapes)) {
    throw new TypeError("transactional spawn shapes must be an array");
  }
  const body = world.createBody(options.body);
  checkedHandle2(body, "transactional spawn body");
  let bodyRegistered = false;
  try {
    const created = [];
    for (const spec of options.shapes) {
      if (spec === null || typeof spec !== "object") {
        throw new TypeError("transactional spawn shape spec must be an object");
      }
      const shape = addSpawnShape(world, body, spec);
      checkedHandle2(shape, "transactional spawn shape");
      created.push({
        kind: spec.kind,
        shape,
        identity: requireShapeIdentity(world, shape, "transactional spawn")
      });
    }
    const bodyRegistration = registry.registerBody(body, options.bodyAddress);
    bodyRegistered = true;
    const shapes = created.map((entry) => {
      const registration = registry.registerShape(entry.shape, body, entry.identity);
      return Object.freeze({
        kind: entry.kind,
        shape: entry.shape,
        identity: entry.identity,
        registration
      });
    });
    return Object.freeze({
      body,
      bodyRegistration,
      shapes: Object.freeze(shapes)
    });
  } catch (error) {
    try {
      if (bodyRegistered) registry.unregisterBody(body);
    } finally {
      world.destroyBody(body);
    }
    throw error;
  }
}
var RegistrationHost = class {
  participantKey;
  disposed = false;
  revisionValue = 0;
  bodies = /* @__PURE__ */ new Set();
  shapes = /* @__PURE__ */ new Map();
  world;
  registry;
  belongsToHost;
  constructor(options) {
    if (options === null || typeof options !== "object") {
      throw new TypeError("registration host options must be an object");
    }
    if (options.world === null || typeof options.world !== "object") {
      throw new TypeError("registration host requires a world");
    }
    if (options.registry === null || typeof options.registry !== "object") {
      throw new TypeError("registration host requires an entity registry");
    }
    if (typeof options.participantKey !== "string" || options.participantKey.length === 0) {
      throw new TypeError("registration host participantKey must be a non-empty string");
    }
    if (options.belongsToHost !== void 0 && typeof options.belongsToHost !== "function") {
      throw new TypeError("registration host belongsToHost must be a function when supplied");
    }
    this.world = options.world;
    this.registry = options.registry;
    this.participantKey = options.participantKey;
    this.belongsToHost = options.belongsToHost;
  }
  get revision() {
    return this.revisionValue;
  }
  registerBody(body, address) {
    this.assertLive();
    checkedHandle2(body, "body");
    if (this.belongsToHost !== void 0 && !this.belongsToHost(address)) {
      throw new Error("body address does not belong to this host");
    }
    const registration = this.registry.registerBody(body, address);
    this.bodies.add(body);
    return registration;
  }
  registerShape(shape, body) {
    this.assertLive();
    checkedHandle2(shape, "shape");
    const bodyNumber = checkedHandle2(body, "body");
    if (!this.bodies.has(bodyNumber)) {
      throw new Error("shape body is not owned by this host");
    }
    try {
      const identity = requireShapeIdentity(this.world, shape, "registration host");
      const registration = this.registry.registerShape(shape, body, identity);
      this.shapes.set(shapeKey2(identity), identity);
      return registration;
    } catch (error) {
      this.unregisterBody(body);
      throw error;
    }
  }
  /** Remove semantic ownership after the native owner destroys the body. */
  unregisterBody(body) {
    const bodyNumber = checkedHandle2(body, "body");
    if (!this.bodies.has(bodyNumber)) return false;
    this.registry.unregisterBody(body);
    this.bodies.delete(bodyNumber);
    for (const [key, identity] of this.shapes) {
      if (this.registry.resolveShape(identity) === void 0) this.shapes.delete(key);
    }
    return true;
  }
  destroyBody(body) {
    const bodyNumber = checkedHandle2(body, "body");
    if (!this.bodies.has(bodyNumber)) return false;
    let firstError;
    try {
      this.world.destroyBody(body);
    } catch (error) {
      firstError = error;
    } finally {
      this.unregisterBody(body);
    }
    if (firstError !== void 0) throw firstError;
    return true;
  }
  /** Mark one successful participant reset; bodies are managed by the runtime. */
  advanceRevision() {
    this.assertLive();
    this.revisionValue += 1;
    return this.revisionValue;
  }
  snapshot() {
    this.assertLive();
    return Object.freeze({
      participantKey: this.participantKey,
      revision: this.revisionValue,
      bodies: Object.freeze(
        [...this.bodies].sort((left, right) => left - right).map((body) => body)
      ),
      shapes: Object.freeze(
        [...this.shapes.values()].sort(
          (left, right) => left.world0 - right.world0 || left.index1 - right.index1 || left.generation - right.generation
        ).map(copyShapeIdentity)
      ),
      disposed: this.disposed
    });
  }
  dispose() {
    if (this.disposed) return;
    const bodies = [...this.bodies];
    let firstError;
    try {
      for (const body of bodies) {
        try {
          this.destroyBody(body);
        } catch (error) {
          if (firstError === void 0) firstError = error;
        }
      }
    } finally {
      this.markDisposed();
    }
    if (firstError !== void 0) throw firstError;
  }
  markDisposed() {
    this.bodies.clear();
    this.shapes.clear();
    this.disposed = true;
  }
  assertLive() {
    if (this.disposed) throw new Error("registration host used after dispose()");
  }
};
function createRegistrationHost(options) {
  return new RegistrationHost(options);
}

// src/index.ts
var VERSION = {
  lib: "0.0.0",
  box3d: "v0.1.0",
  emscripten: "6.0.2"
};
var moduleOfWorld = /* @__PURE__ */ new WeakMap();
var Box3DImpl = class {
  constructor(mod) {
    this.mod = mod;
  }
  mod;
  disposed = false;
  capsCache = null;
  worlds = /* @__PURE__ */ new Set();
  assertLive() {
    if (this.disposed) {
      throw new Error("box3d-web: Box3D instance used after dispose().");
    }
  }
  createWorldImpl(options) {
    this.assertLive();
    const [gravityX, gravityY, gravityZ] = options.gravity ?? [0, -9.81, 0];
    const handle = this.mod.exports.b3bridge_create_world(
      Number.isFinite(gravityX) ? gravityX : 0,
      Number.isFinite(gravityY) ? gravityY : -9.81,
      Number.isFinite(gravityZ) ? gravityZ : 0,
      options.enableSleep === false ? 0 : 1,
      options.enableContinuous === false ? 0 : 1
    );
    const world = new WorldImpl(this.mod, handle);
    moduleOfWorld.set(world, this.mod);
    this.worlds.add(world);
    const originalDestroy = world.destroy.bind(world);
    world.destroy = () => {
      this.worlds.delete(world);
      originalDestroy();
    };
    return world;
  }
  createWorld(options = {}) {
    return this.createWorldImpl(options);
  }
  capabilities() {
    this.assertLive();
    if (this.capsCache) return this.capsCache;
    const world = this.createWorldImpl({});
    try {
      this.capsCache = computeCapabilities(this.mod, world);
    } finally {
      world.destroy();
    }
    return this.capsCache;
  }
  dispose() {
    if (this.disposed) return;
    for (const world of [...this.worlds]) {
      world.destroy();
    }
    this.worlds.clear();
    this.disposed = true;
  }
  get version() {
    return { ...VERSION };
  }
};
async function createBox3D(options) {
  const mod = await loadBox3DModule(options ?? {});
  return new Box3DImpl(mod);
}
function probeCapabilities(world) {
  const impl = world;
  const mod = moduleOfWorld.get(impl);
  if (!mod) {
    throw new TypeError(
      "box3d-web: probeCapabilities received a World not created by createBox3D()."
    );
  }
  return computeCapabilities(mod, impl);
}

export { ArticulatedPoseError, BodyPool, DEFAULT_HULL_MAX_VERTICES, EntityRegistry, FixedStepper, HULL_MAX_VERTICES_LIMIT, MAX_NATIVE_GENERATION, MAX_NATIVE_SHAPE_INDEX, MAX_NATIVE_WORLDS, RegistrationHost, SceneRuntime, SleepManager, TransformBuffer, applyArticulatedPose, createBox3D, createEntityRegistry, createIdentitySystem, createRegistrationHost, createSceneRuntime, probeCapabilities, radialImpulse, sameIdentity, spawnRegistered };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map